/* =========================================================================
   QUICK PRO — build engine (PDP redesign). Fork of assets/qp-quick-ship.js;
   that file stays untouched and keeps serving the live Quick Ship pages
   until the launch flip.
   -------------------------------------------------------------------------
   Reads the recipe blob rendered by snippets/qp-build-recipe.liquid
   (<script type="application/json" data-build-data>) — see that file's
   header for the contract. The single optional "bar" of the Quick Ship
   engine is generalised into components[] with per-mode gating:
     bundle  — every component is REQUIRED; each one with pickers must be
               explicitly CONFIRMED (a size-derived cockpit suggestion
               never counts). marker "bd", _bundle stamped on every line.
     upsell  — components are optional add-ons (SKIP persists); only size
               gates. marker "qs" — emission byte-identical to the Quick
               Ship engine (no _bundle, no Comment).
     plain   — no components; only size gates.

   window.QpBuild — PURE functions, no DOM (tests load this file in jsdom
   and call them directly):
     resolveRoute(data, sel) -> {route:'us'|'factory', frameVariantId,
                                 source:'self'|'twin'}   never "none"
     gate(data, sel)         -> {canBuy, missing[]}
     suggestCockpit(data, size) -> {width, stem} | null
     emitLines(data, sel, ts)   -> /cart/add.js items[]
     persist(handle, sel) / restore(handle, data)   sessionStorage
                                 "qp-build:<handle>"
     bundleToPresentment(cents) -> bundleCents (shop currency, USD) in the
                                 buyer's presentment currency
                                 (Shopify.currency.rate, fallback 1)

   Selection object (sel): { color, size, comment, skipped,
     <key>: {width, stem} | {depth} | {<picker field>: value} | true,
     <key>VariantId, cockpitSuggested }   — <key> = components[].key.
   Picker field = "width" / "stem" / "depth" when the option name contains
   that word, else the lower-cased option name.

   _group_id = <parentVariantId>_<marker>_<epochms> — format LOAD-BEARING:
   qp-cart-group-map.liquid splits on '_' and matches the FIRST segment
   against the parent line's variant id (the twin variant on the factory
   route). No Globo keys are read or written anywhere on this path.

   <qp-build-main> dispatches the same 'qp-qs:state' event as the Quick
   Ship engine (so the pinned <qp-qs-bar> keeps working) with
   { mode: <bundle|upsell|plain>, missing, ship: <us|factory>, color,
   size } added. <qp-qs-bar> and <qp-qs-fit> below are VERBATIM copies
   except these bindings: both look up qp-build-main before qp-qs-main,
   the bar stamps data-ship from detail.ship (not detail.mode), and the
   fit reads the build host through its _mainData() adapter
   ({variants, colorIndex, currentColor} over data.frame / sel.color).
   ========================================================================= */
(function () {
  'use strict';

  var EVT = 'qp-qs:state';
  var STORE_PREFIX = 'qp-build:';

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escAttr(s) { return esc(s).replace(/"/g, '&quot;'); }
  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  /* add a class that removes itself on animationend (or after ms — reduced
     motion runs no animation, so no animationend fires) */
  function oneShot(el, cls, ms) {
    el.classList.remove(cls);
    void el.offsetWidth; /* restart the keyframes on a repeat */
    el.classList.add(cls);
    var done = function () {
      el.classList.remove(cls);
      el.removeEventListener('animationend', done);
      clearTimeout(t);
    };
    var t = setTimeout(done, ms);
    el.addEventListener('animationend', done);
  }
  /* VALUE SETTLE (PM 2026-10-22): every money / label write goes through
     here. Unchanged text is a no-op; changed text is written and the node
     gets a one-shot .is-settling (opacity + 3px rise, CSS, reduced-motion
     guarded). No outgoing animation, aria-live untouched. quiet = the first
     paint, which must never animate. */
  function settle(el, text, quiet) {
    if (!el || el.textContent === text) return false;
    el.textContent = text;
    if (!quiet) oneShot(el, 'is-settling', 320);
    return true;
  }
  /* Frame (priced) mode money. Format = shop.money_format passed through
     the section JSON (settings.moneyFormat) so the TOTAL matches the
     server-rendered prices around it EXACTLY ("$5,879.00" beside
     "$5,499.00 + $380.00" — never the with-currency register that
     MinimogSettings carries when currency codes are enabled). The bare-$
     fallback only exists so a missing format can never blank the line. */
  function fmtMoney(cents, format) {
    var s = (cents / 100).toFixed(2), i = s.indexOf('.');
    var whole = s.slice(0, i).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var amount = whole + s.slice(i);
    if (format && format.indexOf('amount') !== -1) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(/,/g, ' ').replace('.', ',').replace(/ /g, '.'))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole.replace(/,/g, '.'));
    }
    return '$' + amount;
  }

  /* Bundle prices (recipe bundleCents, settings.bundleCurrency "USD") are
     SHOP-currency cents; every other figure on the page (variant prices,
     listCents) is already in the buyer's presentment currency. The Cart
     Transform charges bundle * presentmentCurrencyRate, so the page
     applies the same rate — Shopify.currency.rate (a string; absent or
     junk -> 1, i.e. unchanged). Read at call time: the storefront sets
     Shopify.currency in <head>, before any of this runs. */
  function presentmentRate() {
    var c = window.Shopify && window.Shopify.currency;
    var r = c ? parseFloat(c.rate) : NaN;
    return isFinite(r) && r > 0 ? r : 1;
  }
  function bundleToPresentment(cents) {
    if (cents == null || typeof cents !== 'number') return cents;
    return Math.round(cents * presentmentRate());
  }

  /* TALK TO US chat trigger fallback. The chat runtime (qpu-chat.js)
     owns [data-qp-open-chat] with a document CAPTURE delegate that
     stopPropagation()s, so this bubble listener can only ever fire when
     that script never loaded — degrade to the merchant's fallback URL
     (trust_3_url) instead of a dead click. No third mechanism: same
     attribute, same delegate contract as the footer/nav/cart. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('.qp-qs__trust-chat[data-qp-open-chat]');
    if (!btn) return;
    var fb = btn.getAttribute('data-qs-chat-fallback');
    if (fb) window.location.href = fb;
  });

  /* ======================================================= pure engine */

  /* bare numeric option values ("380") get their unit for humans; values
     that already carry one ("38cm") pass through untouched. SAME rule as
     the Quick Ship engine's _fmtDim — the Cockpit property must stay
     byte-identical to today ("380mm · 100mm"). */
  function fmtDim(s) {
    var v = String(s == null ? '' : s);
    return /^\d+(\.\d+)?$/.test(v) ? v + 'mm' : v;
  }

  function fieldOf(name) {
    var n = String(name == null ? '' : name).toLowerCase();
    if (n.indexOf('width') !== -1) return 'width';
    if (n.indexOf('stem') !== -1) return 'stem';
    if (n.indexOf('depth') !== -1) return 'depth';
    return n;
  }

  function isObj(o) { return !!o && typeof o === 'object' && !Array.isArray(o); }
  function settingsOf(data) { return (data && data.settings) || {}; }
  function componentsOf(data) { return (data && data.components) || []; }

  function frameSelfVariant(data, sel) {
    var f = data.frame || {}, ci = f.colorIndex, si = f.sizeIndex;
    var vs = f.variants || [];
    for (var k = 0; k < vs.length; k++) {
      var v = vs[k];
      if (ci >= 0 && v.options[ci] !== sel.color) continue;
      if (si >= 0 && v.options[si] !== sel.size) continue;
      return v;
    }
    return null;
  }

  /* twin products carry a Color option only — size rides as a property */
  function twinVariant(data, color) {
    var t = data.frame && data.frame.twin;
    if (!t || !t.variants) return null;
    for (var k = 0; k < t.variants.length; k++) {
      if ((t.variants[k].options || []).indexOf(color) !== -1) return t.variants[k];
    }
    return null;
  }

  function variantById(list, id) {
    if (id == null) return null;
    for (var k = 0; k < (list || []).length; k++) {
      if (String(list[k].id) === String(id)) return list[k];
    }
    return null;
  }

  /* the component variant matching a {field: value} choice — every picker
     must be answered and the combo must exist */
  function componentVariantFor(comp, choice) {
    if (!isObj(choice) || !comp.pickers || !comp.pickers.length) return null;
    for (var k = 0; k < comp.variants.length; k++) {
      var v = comp.variants[k], hit = true;
      for (var p = 0; p < comp.pickers.length; p++) {
        var pk = comp.pickers[p];
        if (synthetic(pk)) continue; /* a spec picker never matches a variant */
        var want = choice[fieldOf(pk.name)];
        if (want == null || v.options[pk.optionIndex] !== String(want)) { hit = false; break; }
      }
      if (hit) return v;
    }
    return null;
  }

  /* a synthetic picker (optionIndex < 0) is not a product option: its
     values come from comp.specs[<the staged value of dependsOn>] */
  function synthetic(pk) { return !!pk && typeof pk.optionIndex === 'number' && pk.optionIndex < 0; }

  /* the combos a synthetic picker offers for a staged choice: the recipe
     specs of the choice's dependsOn value; [] when none is listed */
  function specValues(comp, pk, choice) {
    var dep = fieldOf(pk && pk.dependsOn);
    var key = isObj(choice) ? choice[dep] : null;
    var specs = comp && isObj(comp.specs) ? comp.specs : null;
    var list = key != null && specs && Array.isArray(specs[String(key)]) ? specs[String(key)] : [];
    return list;
  }

  /* every synthetic picker is answered with one of its listed combos (a
     picker with no combos listed needs no answer) */
  function specAnswered(comp, choice) {
    var pks = comp.pickers || [];
    for (var p = 0; p < pks.length; p++) {
      if (!synthetic(pks[p])) continue;
      var vals = specValues(comp, pks[p], choice);
      if (!vals.length) continue;
      var got = isObj(choice) ? choice[fieldOf(pks[p].name)] : null;
      if (got == null || vals.indexOf(String(got)) === -1) return false;
    }
    return true;
  }

  /* the CONFIRMED pick for a component: an explicit {field: value} choice,
     or an explicit variant id. sel.<key>Suggested never counts. A
     pickerless component may be chosen with sel[key] === true. */
  function componentPick(comp, sel) {
    var choice = sel[comp.key];
    var v = componentVariantFor(comp, choice);
    if (v) return v;
    v = variantById(comp.variants, sel[comp.key + 'VariantId']);
    if (v) return v;
    if (choice === true && (!comp.pickers || !comp.pickers.length)) return comp.variants[0] || null;
    return null;
  }

  /* merchant rule 2026-08-17: a component variant is buyable when it is
     US-stocked or has positive quantity */
  function buyable(v) {
    return !!v && (v.us === true || (typeof v.qty === 'number' && v.qty > 0));
  }

  function offFrameColor(data, color) {
    var f = data.frame || {}, ci = f.colorIndex;
    if (ci == null || ci < 0 || color == null) return false;
    return !(f.variants || []).some(function (v) { return v.options[ci] === color; });
  }

  /* frame.colors (recipe) when present, else the frame variants' values */
  function knownColor(data, color) {
    var f = (data && data.frame) || {};
    if ((f.colors || []).some(function (c) { return c && c.value === color; })) return true;
    return (f.variants || []).some(function (v) { return v.options[f.colorIndex] === color; });
  }

  function resolveRoute(data, sel) {
    sel = sel || {};
    var ps = settingsOf(data).previewState;
    var f = data.frame || {};
    var selfV = frameSelfVariant(data, sel);
    var tv = twinVariant(data, sel.color);
    /* PM 2026-10-25: a Quick Ship (_qs_) page adds a built-to-order size IN
       PLACE like the bundle does — the twin variant of the chosen colour with
       the verbatim factory keys (Size code, Fork when the frame has one, …).
       The twin PAGE route (frameVariantId null, nothing to buy here) is now
       only the fallback for a colour the twin does not carry. */
    /* fixwave 34 (merchant ruling 2026-10-25): qs_oos_mode = link_out makes the
       twin PAGE-ONLY on a Quick Ship page — a size not in US stock is never
       bought here, the shopper is sent to the configurable page (sel.route,
       the old switch, is ignored) */
    var linkOut = data.marker === 'qs' && settingsOf(data).qsOosMode === 'link_out';
    var qsTwinPage = data.marker === 'qs' && !!f.twin && (!tv || linkOut);
    var TWIN_PAGE = { route: 'factory', frameVariantId: null, source: 'twinUrl' };
    /* fixwave 33: a merchant rule — the page always posts the normal
       (made-to-order) frame, never Quick Ship stock. It outranks every other
       rule (force_us and the shopper's sel.route included). */
    if (settingsOf(data).frameSource === 'made_to_order' && tv) {
      return { route: 'factory', frameVariantId: tv.id, source: 'twin' };
    }
    if (ps === 'force_factory' && (qsTwinPage || tv)) {
      return qsTwinPage ? TWIN_PAGE : { route: 'factory', frameVariantId: tv.id, source: 'twin' };
    }
    /* a colourway the QS frame does not carry at all (bundle products
       list the twin-only colours in frame.colors) goes straight to the
       twin — no size of it can ever ship from the US frame */
    if (offFrameColor(data, sel.color) && (qsTwinPage || tv)) {
      return qsTwinPage ? TWIN_PAGE : { route: 'factory', frameVariantId: tv.id, source: 'twin' };
    }
    /* PM 2026-10-25: the HOW IT SHIPS switch — a shopper who chose built to
       order gets the in-page twin line even when the size is in US stock
       (a chosen size only; force_us preview still wins) */
    if (!linkOut && sel.route === 'factory' && tv && ps !== 'force_us' && (f.sizeIndex < 0 || sel.size)) {
      return { route: 'factory', frameVariantId: tv.id, source: 'twin' };
    }
    if (selfV && (selfV.available || ps === 'force_us')) {
      return { route: 'us', frameVariantId: selfV.id, source: 'self' };
    }
    /* size not chosen yet: say what the colour CAN do without flipping
       the page to the factory route while a boxed size exists */
    if (!selfV && f.sizeIndex >= 0 && !sel.size) {
      var ci = f.colorIndex;
      var boxed = (f.variants || []).some(function (v) {
        return (ci < 0 || v.options[ci] === sel.color) && (v.available || ps === 'force_us');
      });
      if (boxed || !(qsTwinPage || tv)) return { route: 'us', frameVariantId: null, source: 'self' };
    }
    if (qsTwinPage) return TWIN_PAGE;
    if (tv) return { route: 'factory', frameVariantId: tv.id, source: 'twin' };
    return { route: 'us', frameVariantId: selfV ? selfV.id : null, source: 'self' };
  }

  function gate(data, sel) {
    sel = sel || {};
    var missing = [];
    var f = data.frame || {};
    if (f.sizeIndex >= 0 && !sel.size) missing.push('size');
    if (data.mode === 'bundle') {
      componentsOf(data).forEach(function (c) {
        if (c.required && c.pickers && c.pickers.length && !componentPick(c, sel)) missing.push(c.key);
      });
    }
    var r = resolveRoute(data, sel);
    /* a _bd_ factory line without a size_code would emit a blank Size —
       not the customer's fault, nothing they can pick: block the ATC
       up front instead of failing after submit */
    if (r.source === 'twin' && sel.size && !(f.sizeCodes || {})[sel.size]) missing.push('size_code');
    /* twinUrl (qs → twin PAGE) carries no frameVariantId: never buyable */
    var routeOk = r.frameVariantId != null;
    if (routeOk && r.source === 'self' && settingsOf(data).previewState !== 'force_us') {
      /* no twin to fall back on and the self variant is sold out: the
         route "resolves" (never none) but nothing can be bought */
      var sv = variantById(f.variants, r.frameVariantId);
      routeOk = !!(sv && sv.available);
    }
    return { canBuy: missing.length === 0 && routeOk, missing: missing };
  }

  function parseSuggest(data) {
    var raw = settingsOf(data).sizeSuggest;
    if (isObj(raw)) return raw;
    if (typeof raw !== 'string' || !raw) return {};
    try {
      var o = JSON.parse(raw);
      return isObj(o) ? o : {};
    } catch (e) { return {}; }
  }

  function suggestCockpit(data, size) {
    if (size == null) return null;
    var e = parseSuggest(data)[size];
    if (!isObj(e) || e.width == null || e.stem == null || e.width === '' || e.stem === '') return null;
    return { width: String(e.width), stem: String(e.stem) };
  }

  /* [{comp, variant}] that go in the cart, in blob order */
  function chosenComponents(data, sel) {
    var out = [];
    var comps = componentsOf(data);
    if (data.mode === 'bundle') {
      comps.forEach(function (c) {
        var v = componentPick(c, sel);
        if (!v && (!c.pickers || !c.pickers.length)) v = c.variants[0] || null;
        if (!v) throw new Error('qp-build: bundle component "' + c.key + '" not confirmed');
        out.push({ comp: c, variant: v });
      });
      return out;
    }
    if (data.mode !== 'upsell' || sel.skipped) return out;
    comps.forEach(function (c) {
      var v = componentPick(c, sel);
      if (v) out.push({ comp: c, variant: v });
    });
    return out;
  }

  function pickerValue(comp, variant, field) {
    for (var p = 0; p < (comp.pickers || []).length; p++) {
      if (fieldOf(comp.pickers[p].name) === field) return variant.options[comp.pickers[p].optionIndex];
    }
    return null;
  }

  /* the first option value of a variant that is not the lone default */
  function firstOption(variant) {
    var o = (variant.options || []).filter(function (x) { return x && x !== 'Default Title'; });
    return o.length ? o[0] : null;
  }

  /* choice = the confirmed {field: value} (sel[comp.key]) — only the
     groupset reads it (its drivetrain spec). `more` = extra properties
     the add-on line carries after the main one. */
  function addonLabel(comp, variant, choice) {
    if (comp.key === 'groupset') {
      var gname = pickerValue(comp, variant, 'title');
      if (gname == null) gname = firstOption(variant);
      if (gname == null) return null;
      var g = { k: 'Group Set', v: String(gname) };
      var combo = isObj(choice) ? choice['drivetrain options'] : null;
      if (combo != null && String(combo).trim() !== '') g.more = { 'Drivetrain Options': String(combo) };
      return g;
    }
    if (comp.key === 'power_meter') {
      var crank = pickerValue(comp, variant, 'crankset');
      if (crank == null) crank = firstOption(variant);
      var pm = { k: 'Power Meter', v: comp.title };
      if (crank != null) pm.more = { 'Power Meter Spec': String(crank) };
      return pm;
    }
    if (comp.key === 'bottom_bracket') {
      var raw = firstOption(variant);
      var up = raw == null ? '' : String(raw).toUpperCase();
      var brand = up.indexOf('SHIMANO') === 0 ? 'SHIMANO' : up.indexOf('SRAM') === 0 ? 'SRAM' : raw;
      if (brand == null) return null;
      return { k: 'Bottom Bracket', v: 'With Bottom Bracket - ' + brand + ' Spec Ceramic' };
    }
    if (comp.key === 'cockpit') {
      return { k: 'Cockpit', v: fmtDim(pickerValue(comp, variant, 'width')) + ' · ' + fmtDim(pickerValue(comp, variant, 'stem')) };
    }
    if (comp.key === 'wheelset') {
      var d = pickerValue(comp, variant, 'depth');
      if (d == null && comp.pickers && comp.pickers.length) d = variant.options[comp.pickers[0].optionIndex];
      return { k: 'Wheelset', v: String(d) };
    }
    var vals = (variant.options || []).filter(function (o) { return o && o !== 'Default Title'; });
    return vals.length ? { k: comp.title, v: vals.join(' · ') } : null;
  }

  /* the Globo spelling of a groupset on a built-to-order frame line
     (recipe orderNames, from the groupset_order_names setting): runs of
     spaces folded on both sides; null = no line, post the variant name */
  function orderName(comp, name) {
    var map = (comp && comp.orderNames) || {};
    var want = String(name).replace(/\s+/g, ' ').trim();
    for (var k in map) {
      if (Object.prototype.hasOwnProperty.call(map, k) && String(k).replace(/\s+/g, ' ').trim() === want) return map[k];
    }
    return null;
  }

  function put(props, k, v) {
    if (v == null || String(v).trim() === '') return;
    props[k] = v;
  }

  /* "ZEPHYR SSL Handlebar" -> "ZEPHYR SSL" (Globo-era Handle Bar value) */
  function handleBarName(title) {
    return String(title == null ? '' : title).replace(/\s+handlebar\s*$/i, '');
  }

  /* merchant rule 2026-10-22: the WHOLE build is ready ('us' — ships from
     the US warehouse now) only when the frame route is US and every CHOSEN
     component variant is US-stocked (v.us, the custom.us_stock flag; a
     positive factory qty does not count). Anything else is 'wait' (the
     factory window). Parts not yet chosen do not vote; an upsell with the
     rail skipped is the frame alone. Never throws. */
  function readiness(data, sel) {
    sel = sel || {};
    var r = resolveRoute(data, sel);
    if (r.route !== 'us') return 'wait';
    if (data.mode !== 'bundle' && (data.mode !== 'upsell' || sel.skipped)) return 'us';
    var comps = componentsOf(data);
    for (var i = 0; i < comps.length; i++) {
      var c = comps[i];
      var v = componentPick(c, sel);
      if (!v && data.mode === 'bundle' && (!c.pickers || !c.pickers.length)) v = c.variants[0] || null;
      if (v && v.us !== true) return 'wait';
    }
    return 'us';
  }

  /* WHY the build waits (PM bug 2026-10-25: a US-stocked frame with a part
     that is not US-stocked read "Size L is built to your order on the
     configurable page" — the frame cause's copy). 'frame' = the frame route
     itself is not US stock; 'parts' = the frame ships from US stock but a
     CHOSEN part does not (part = that part's title, the first one found). */
  function readinessWhy(data, sel) {
    sel = sel || {};
    if (readiness(data, sel) !== 'wait') return { why: null, part: null };
    var r = resolveRoute(data, sel);
    if (r.route !== 'us') return { why: 'frame', part: null };
    var comps = componentsOf(data);
    for (var i = 0; i < comps.length; i++) {
      var c = comps[i];
      var v = componentPick(c, sel);
      if (!v && data.mode === 'bundle' && (!c.pickers || !c.pickers.length)) v = c.variants[0] || null;
      if (v && v.us !== true) return { why: 'parts', part: c.title || null };
    }
    return { why: 'frame', part: null };
  }

  /* the recipe's listed values for one free factory spec key (Fork /
     Seatpost Length / Seatpost Offset); [] when the frame has no such choice */
  function factoryValues(data, key) {
    var list = (data && data.factoryOptions) || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].key === key) return Array.isArray(list[i].values) ? list[i].values : [];
    }
    return [];
  }

  /* shopper's pick when it is one of the listed values, else the recipe
     default (factoryProps — the first listed value). A frame with no
     option for the key never carries one, whatever sel.factory says. */
  function factoryChoice(data, sel, key) {
    var dflt = (data.factoryProps || {})[key];
    var picked = sel && isObj(sel.factory) ? sel.factory[key] : null;
    if (typeof picked === 'string' && factoryValues(data, key).indexOf(picked) !== -1) return picked;
    return dflt;
  }

  function emitLines(data, sel, ts) {
    sel = sel || {};
    var r = resolveRoute(data, sel);
    if (r.source === 'twinUrl') throw new Error('qp-build: quick ship routes to the twin page — no cart lines');
    if (r.frameVariantId == null) throw new Error('qp-build: no frame variant for this selection');
    var gid = r.frameVariantId + '_' + data.marker + '_' + ts;
    var bd = data.marker === 'bd';
    function stamp(type) {
      var p = { '_group_id': gid, '_type': type };
      /* _bundle rides EVERY line of a _bd_ build, NEVER a _qs_ line */
      if (bd && data.bundleHandle) p['_bundle'] = data.bundleHandle;
      return p;
    }
    var picks = chosenComponents(data, sel);
    var cockpitPick = null;
    picks.forEach(function (pk) { if (pk.comp.key === 'cockpit') cockpitPick = pk; });

    var parent = stamp('parent');
    if (r.route === 'factory') {
      /* verbatim frameset (config twin) keys — fulfilment reads these;
         "Width " / "Stem " carry a TRAILING SPACE on purpose */
      var code = (data.frame.sizeCodes || {})[sel.size];
      if (!code) throw new Error('qp-build: no size_code for size "' + sel.size + '" — refusing a blank Size');
      var fp = data.factoryProps || {};
      parent['Size'] = code;
      put(parent, 'Fork', factoryChoice(data, sel, 'Fork'));
      put(parent, 'Seatpost Length', factoryChoice(data, sel, 'Seatpost Length'));
      put(parent, 'Seatpost Offset', factoryChoice(data, sel, 'Seatpost Offset'));
      if (cockpitPick) {
        put(parent, 'Handle Bar', handleBarName(cockpitPick.comp.title));
        put(parent, 'Width ', fmtDim(pickerValue(cockpitPick.comp, cockpitPick.variant, 'width')));
        put(parent, 'Stem ', fmtDim(pickerValue(cockpitPick.comp, cockpitPick.variant, 'stem')));
      } else {
        /* a factory frame without a bar: every live frameset order says so
           explicitly (Globo "Handle Bar: No Handle Bar"), never omits the key */
        put(parent, 'Handle Bar', 'No Handle Bar');
      }
      /* a chosen priced upgrade replaces its "No …" default (fixwave 26);
         the spec keys sit right after their part, like live orders */
      var upg = {};
      picks.forEach(function (pk) { upg[pk.comp.key] = pk; });
      var gsLbl = upg.groupset ? addonLabel(upg.groupset.comp, upg.groupset.variant, sel.groupset) : null;
      if (gsLbl) {
        /* SRAM sets are spelled differently in Globo (2026-10-26): the
           frame line takes Globo's value + drivetrain key; add-on lines keep the variant name */
        var gsOrder = orderName(upg.groupset.comp, gsLbl.v);
        put(parent, 'Group Set', gsOrder ? gsOrder.value : gsLbl.v);
        if (gsLbl.more) put(parent, gsOrder ? gsOrder.key : gsLbl.v + ' Drivetrain Options', gsLbl.more['Drivetrain Options']);
      } else {
        put(parent, 'Group Set', fp['Group Set']);
      }
      var pmLbl = upg.power_meter ? addonLabel(upg.power_meter.comp, upg.power_meter.variant, sel.power_meter) : null;
      if (pmLbl) {
        put(parent, 'Power Meter', pmLbl.v);
        if (pmLbl.more) put(parent, 'Power Meter Spec', pmLbl.more['Power Meter Spec']);
      } else {
        put(parent, 'Power Meter', fp['Power Meter']);
      }
      var bbLbl = upg.bottom_bracket ? addonLabel(upg.bottom_bracket.comp, upg.bottom_bracket.variant, sel.bottom_bracket) : null;
      if (bbLbl) put(parent, 'Bottom Bracket', bbLbl.v);
      else put(parent, 'Bottom Bracket', fp['Bottom Bracket']);
    }
    /* the optional order note rides the PARENT line on every route (same key the live Quick Ship contract carries); blank = no key */
    var c = String(sel.comment == null ? '' : sel.comment).trim();
    if (c) parent['Comment'] = c;
    var items = [{ id: r.frameVariantId, quantity: 1, properties: parent }];
    picks.forEach(function (pk) {
      var props = stamp('addon');
      var lbl = addonLabel(pk.comp, pk.variant, sel[pk.comp.key]);
      if (lbl) {
        props[lbl.k] = lbl.v;
        if (lbl.more) Object.keys(lbl.more).forEach(function (mk) { props[mk] = lbl.more[mk]; });
      }
      items.push({ id: pk.variant.id, quantity: 1, properties: props });
    });
    return items;
  }

  function storage() {
    try { return window.sessionStorage || null; } catch (e) { return null; }
  }

  function persist(handle, sel) {
    var s = storage();
    if (!s) return;
    var copy = {};
    Object.keys(sel || {}).forEach(function (k) {
      if (!/Suggested$/.test(k)) copy[k] = sel[k];
    });
    try { s.setItem(STORE_PREFIX + handle, JSON.stringify(copy)); } catch (e) { /* quota/private mode */ }
  }

  /* only what still exists in THIS blob survives: a variant id or a
     component choice the recipe no longer carries is dropped */
  function restore(handle, data) {
    var s = storage(), raw = null;
    if (!s) return {};
    try { raw = JSON.parse(s.getItem(STORE_PREFIX + handle)); } catch (e) { return {}; }
    if (!isObj(raw)) return {};
    var out = {};
    var f = (data && data.frame) || {};
    var vs = f.variants || [];
    function hasOpt(i, val) {
      return vs.some(function (v) { return v.options[i] === val; });
    }
    if (typeof raw.color === 'string' && (f.colorIndex < 0 || knownColor(data, raw.color))) out.color = raw.color;
    if (typeof raw.size === 'string' && (f.sizeIndex < 0 || hasOpt(f.sizeIndex, raw.size))) out.size = raw.size;
    if (raw.skipped === true) out.skipped = true;
    if (raw.route === 'us' || raw.route === 'factory') out.route = raw.route;
    if (typeof raw.comment === 'string') out.comment = raw.comment;
    /* free factory spec picks survive only while the recipe still lists the value */
    if (isObj(raw.factory)) {
      var fac = {};
      ['Fork', 'Seatpost Length', 'Seatpost Offset'].forEach(function (k) {
        if (typeof raw.factory[k] === 'string' && factoryValues(data, k).indexOf(raw.factory[k]) !== -1) fac[k] = raw.factory[k];
      });
      if (Object.keys(fac).length) out.factory = fac;
    }
    componentsOf(data).forEach(function (c) {
      var ch = raw[c.key];
      if (isObj(ch) && componentVariantFor(c, ch) && specAnswered(c, ch)) out[c.key] = ch;
      else if (ch === true && (!c.pickers || !c.pickers.length)) out[c.key] = true;
      var idKey = c.key + 'VariantId';
      if (raw[idKey] != null && variantById(c.variants, raw[idKey])) out[idKey] = raw[idKey];
    });
    return out;
  }

  window.QpBuild = {
    resolveRoute: resolveRoute,
    frameSelfVariant: frameSelfVariant,
    gate: gate,
    suggestCockpit: suggestCockpit,
    emitLines: emitLines,
    factoryValues: factoryValues,
    factoryChoice: factoryChoice,
    readiness: readiness,
    readinessWhy: readinessWhy,
    persist: persist,
    restore: restore,
    fieldOf: fieldOf,
    specValues: specValues,
    specAnswered: specAnswered,
    fmtDim: fmtDim,
    bundleToPresentment: bundleToPresentment
  };

  /* ---------------------------------------------------------------- main */
  /* DOM hooks (rendered by sections/qp-build-main.liquid + snippets/
     qp-build-cards.liquid / qp-build-picker.liquid): [data-build-data],
     the one product form, [data-build-row="size|cockpit|wheelset"],
     [data-build-atc], [data-build-topline], [data-build-slot],
     [data-build-selector="<key>"], [data-build-picker="<key>"],
     [data-build-chip][data-option][value], [data-build-skip],
     [data-build-stub], [data-build-cards], [data-build-rail-cards],
     [data-build-card="<key>"] (+ [data-build-add], [data-build-suggest],
     [data-build-selector-value]), [data-build-base-meta|price],
     [data-build-picker-done|close|note], [data-build-open].

     Cards may render TWICE (upsell: buy box + desktop rail) — every card
     write is a querySelectorAll('[data-build-card="<key>"]') pass.

     Picker (_openPicker / _closePicker): the selector opens its <dialog>
     with showModal() — a bottom sheet <= 768px (CSS), above that a
     popover placed under the selector (.qp-build__picker--popover). Chips
     only stage; opening pre-highlights pending -> confirmed -> the size
     suggestion WITHOUT confirming (§5.5). DONE confirms (all pickers
     answered + the combo exists, else the picker stays open with a note);
     the close button, Esc and a backdrop click dismiss without confirming.
     Focus returns to the selector that opened it (R15). Upsell ADD rings
     toggle a part: ADD opens its picker (nothing is added until DONE),
     a second press removes it. */
  var Q = window.QpBuild;
  if (!customElements.get('qp-build-main')) {
    customElements.define('qp-build-main', class extends HTMLElement {
      connectedCallback() {
        if (this._initDone) return;
        this._initDone = true;
        /* page entrance (buy-box head): tell the inline gate the engine ran
           (its 3s fail-safe stands down), then let the head arrive. All
           hidden states live under html.qp-pdp-enter in qp-build.css. */
        window.__qpPdpEnter = true;
        var self = this;
        var enter = function () { self.classList.add('is-in'); };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(function () { requestAnimationFrame(enter); });
        else enter();
        onReady(this._setup.bind(this));
      }

      _setup() {
        var dataEl = this.querySelector('[data-build-data]');
        if (!dataEl) return;
        try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
        this.s = this.data.settings || {};
        this.storeKey = this.data.bundleHandle || (this.data.frame && this.data.frame.handle) || 'pdp';
        this.sel = Q.restore(this.storeKey, this.data);
        this._pending = {};
        this._added = {};
        this._openKey = null;
        this._opener = null;
        var f = this.data.frame || {};
        if (!this.sel.color && f.colorIndex >= 0 && f.variants && f.variants.length) {
          var first = f.variants.filter(function (v) { return v.available; })[0] || f.variants[0];
          this.sel.color = first.options[f.colorIndex];
        }
        this._suggest();
        /* NEVER name these `slot` / `form` / `title` / `id` / `hidden`:
           HTMLElement reflects those as attributes/strings. */
        this.slotEl = this.querySelector('[data-build-slot]');
        this.formEl = this.querySelector('form');
        this.atc = this.querySelector('[data-build-atc]');
        this.idInput = this.formEl ? this.formEl.querySelector('input[name="id"]') : null;
        this.toplineEl = this.querySelector('[data-build-topline]');
        this.cardsEl = this.querySelector('[data-build-cards]');
        this.stubEl = this.querySelector('[data-build-stub]');
        this.railCardsEl = this.querySelector('[data-build-rail-cards]');
        this.commentEl = this.querySelector('[data-build-comment], [data-qs-comment]');
        /* _qs_ twin-PAGE route: the ATC swaps for this link (today's mto) */
        this.twinCta = this.querySelector('[data-build-twin-cta], [data-qs-twin-cta]');
        /* route-change note under the SIZE row: [hidden] is the no-JS first
           paint only — the engine owns it from here via .is-open so the
           open/close transition can run */
        this.routeNoteEl = this.querySelector('[data-build-route-note]');
        this.routeNoteTextEl = this.routeNoteEl ? this.routeNoteEl.querySelector('[data-build-route-note-text]') : null;
        this.routeNoteEyebrowEl = this.routeNoteEl ? this.routeNoteEl.querySelector('[data-build-route-note-eyebrow]') : null;
        this._factoryEyebrow = this.routeNoteEyebrowEl ? this.routeNoteEyebrowEl.textContent : '';
        /* HOW IT SHIPS switch: the why-line copy is the {size}/{color} template */
        this.routeBlockEl = this.querySelector('[data-build-route]');
        this.routeWhyEl = this.querySelector('[data-build-route-why]');
        this._routeWhyTpl = this.routeWhyEl ? this.routeWhyEl.textContent : '';
        this.legendEl = this.querySelector('.qp-build__legend');
        /* fixwave 34: Quick Ship link_out — two-tier buttons, no route switch */
        this._linkOut = this.data.marker === 'qs' && this.s.qsOosMode === 'link_out';
        /* readiness subs: {size} / {mto_window} templates, re-rendered on sync */
        this.slotSubEls = Array.prototype.slice.call(this.querySelectorAll('[data-build-slot-sub]'));
        if (this.routeNoteEl) this.routeNoteEl.hidden = false;
        this._pulseNote = false;
        this._adding = false;
        /* back-forward cache: coming back from the cart restores the page
           mid-"ADDING…" with the buttons disabled — clear it and restate the
           true button state (guardian note 2026-10-25) */
        if (!this._pageshowBound) {
          this._pageshowBound = true;
          var host = this;
          window.addEventListener('pageshow', function (e) {
            if (!e || !e.persisted) return;
            host._adding = false;
            if (typeof host._unbusy === 'function') host._unbusy();
            host._render(false);
          });
        }
        /* bundle mode renders the ATC disabled (+ <noscript> copy): the
           no-JS form could only post the bare frame. The blob parsed, so
           the engine owns submit now — _render restates the real state. */
        if (this.atc) this.atc.disabled = false;

        this.addEventListener('click', function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          var chip = t.closest('[data-build-chip]');
          if (chip && this.contains(chip)) { this._pickChip(chip); return; }
          var rtab = t.closest('[data-build-route-tab]');
          if (rtab && this.contains(rtab)) { this._pickRoute(rtab); return; }
          var jump = t.closest('[data-build-total-jump]');
          if (jump && this.contains(jump)) { this._jumpToCards(); return; }
          var fchip = t.closest('[data-build-factory-chip]');
          if (fchip && this.contains(fchip)) { this._pickFactory(fchip); return; }
          var pk =t.closest('[data-build-picker]');
          var pkKey = pk ? pk.getAttribute('data-build-picker') : null;
          if (pk && t === pk) { this._closePicker(pkKey, false); return; } /* backdrop */
          if (pk && t.closest('[data-build-picker-done]')) { this._closePicker(pkKey, true); return; }
          if (pk && t.closest('[data-build-picker-close]')) { this._closePicker(pkKey, false); return; }
          if (pk && t.closest('[data-qs-fit-open]')) { this._fitFromPicker(pkKey); return; }
          var add = t.closest('[data-build-add]');
          if (add && this.contains(add)) { this._toggleAdd(add.getAttribute('data-build-add'), add); return; }
          var selBtn = t.closest('[data-build-selector]');
          if (selBtn && this.contains(selBtn)) { this._selectorClick(selBtn.getAttribute('data-build-selector'), selBtn); return; }
          var reopen = t.closest('[data-build-open]');
          if (reopen && this.contains(reopen)) {
            var rk = reopen.getAttribute('data-build-open');
            var rcard = reopen.closest('[data-build-card]');
            this._openPicker(rk, (rcard && rcard.querySelector('[data-build-selector]')) || reopen);
            return;
          }
          var skip = t.closest('[data-build-skip]');
          if (skip && this.contains(skip)) { this._setSkipped(true); return; }
          var stub = t.closest('[data-build-stub]');
          if (stub && this.contains(stub)) { this._setSkipped(false); }
        }.bind(this));

        /* Esc: native <dialog> fires 'cancel'; the keydown path covers
           engines without showModal (and is idempotent with cancel) */
        this.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && this._openKey) {
            e.preventDefault();
            this._closePicker(this._openKey, false);
          }
        }.bind(this));
        var self = this;
        this.querySelectorAll('dialog[data-build-picker]').forEach(function (d) {
          var key = d.getAttribute('data-build-picker');
          d.addEventListener('cancel', function (e) {
            e.preventDefault();
            if (self._openKey === key) self._closePicker(key, false);
          });
          /* closed by anything else (form[method=dialog], the UA): restate */
          d.addEventListener('close', function () {
            if (self._openKey === key) self._closePicker(key, false);
          });
        });
        var replace = function () {
          if (!self._openKey) return;
          var d = self._pickerEl(self._openKey);
          if (d && d.classList.contains('qp-build__picker--popover')) self._place(d, self._opener);
        };
        window.addEventListener('resize', replace);
        window.addEventListener('scroll', replace, { passive: true });

        if (this.formEl) this.formEl.addEventListener('submit', this._submit.bind(this));
        this._render(true);
      }

      _suggest() {
        this.sel.cockpitSuggested = Q.suggestCockpit(this.data, this.sel.size);
      }

      /* previewState force_* treats every frame variant as purchasable —
         same rule as qp-quick-ship.js _avail (and the section's chips) */
      _avail(v) {
        var ps = this.s.previewState;
        return ps === 'force_us' || ps === 'force_factory' ? true : !!(v && v.available);
      }

      /* <qp-qs-fit> adapter: the Quick Ship host exposes data.variants /
         data.colorIndex / selection[]; the build host keeps them under
         data.frame and sel.color. */
      get variants() { return (this.data && this.data.frame && this.data.frame.variants) || []; }
      _mainData() {
        var f = (this.data && this.data.frame) || {};
        if (!f.variants || !f.variants.length) return null;
        return { variants: f.variants, colorIndex: f.colorIndex, currentColor: (this.sel && this.sel.color) || null };
      }

      _save() { Q.persist(this.storeKey, this.sel); }

      /* frame chips (data-option contains color/size) write straight into
         the selection; chips inside a picker dialog only stage a PENDING
         choice — the component counts once confirmComponent() runs. */
      _pickChip(btn) {
        var opt = btn.getAttribute('data-option') || '';
        var val = btn.getAttribute('value');
        if (val == null) return;
        var picker = btn.closest('[data-build-picker]');
        if (picker) {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          var key = picker.getAttribute('data-build-picker');
          var base = this._highlighted(key) || {};
          var next = {};
          Object.keys(base).forEach(function (k) { next[k] = base[k]; });
          var optField = Q.fieldOf(opt);
          var changed = String(next[optField]) !== String(val);
          next[optField] = val;
          /* a synthetic picker depending on this one restarts on a new value */
          var pcomp0 = this._comp(key);
          ((pcomp0 && pcomp0.pickers) || []).forEach(function (pk) {
            if (!synthetic(pk)) return;
            var f = Q.fieldOf(pk.name);
            if (Q.fieldOf(pk.dependsOn) === optField && changed) delete next[f];
          });
          this._pending[key] = next;
          this._render(false);
          return;
        }
        var lc = opt.toLowerCase();
        if (lc.indexOf('color') !== -1 || lc.indexOf('colour') !== -1) this.sel.color = val;
        else if (lc.indexOf('size') !== -1) { this.sel.size = val; this._suggest(); this._pulseNote = true; }
        else return;
        this._save();
        this._render(false);
        this._pulseNote = false;
      }

      /* HOW IT SHIPS tab: the shopper's route choice. The disabled US tab
         (size not in US stock) does nothing. */
      _pickRoute(btn) {
        if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
        var v = btn.getAttribute('data-build-route-tab');
        if (v !== 'us' && v !== 'factory') return;
        this.sel.route = v;
        this._save();
        this._render(false);
      }

      /* the switch shows once a size is chosen. No US stock for colour+size
         (and not force_us) -> US tab disabled, factory selected, the why-line
         says so; otherwise the shopper's sel.route decides. */
      _syncRoute(st) {
        var blk = this.routeBlockEl;
        if (!blk) return;
        var show = !!this.sel.size && !this._linkOut; /* link_out: no switch, whatever the size */
        if (blk.hidden === show) blk.hidden = !show;
        var ps = (this.s && this.s.previewState) || 'live';
        var sv = Q.frameSelfVariant(this.data, this.sel);
        var inStock = ps === 'force_us' || !!(sv && sv.available);
        var lockUs = !inStock || ps === 'force_factory';
        var self = this;
        blk.querySelectorAll('[data-build-route-tab]').forEach(function (b) {
          var which = b.getAttribute('data-build-route-tab');
          var on = which === st.ship;
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.classList.toggle('is-selected', on);
          if (which === 'us') {
            b.disabled = lockUs;
            b.setAttribute('aria-disabled', lockUs ? 'true' : 'false');
          }
        });
        if (this.routeWhyEl) {
          /* made-to-order frame source: there is no route to explain */
          var mto = !!(this.s && this.s.frameSource === 'made_to_order');
          var showWhy = show && !inStock && !mto;
          if (showWhy) {
            var text = this._routeWhyTpl
              .split('{size}').join(this.sel.size || '')
              .split('{color}').join(this.sel.color || '');
            if (this.routeWhyEl.textContent !== text) this.routeWhyEl.textContent = text;
          }
          if (this.routeWhyEl.hidden === showWhy) this.routeWhyEl.hidden = !showWhy;
        }
      }

      /* BUILT TO ORDER SPEC chip: a free factory spec choice. Only a value
         the recipe lists counts; the line posts it through emitLines. */
      _pickFactory(btn) {
        var key = btn.getAttribute('data-build-factory-chip');
        var val = btn.getAttribute('value');
        if (!key || val == null) return;
        if (Q.factoryValues(this.data, key).indexOf(val) === -1) return;
        var next = {};
        var cur = isObj(this.sel.factory) ? this.sel.factory : {};
        Object.keys(cur).forEach(function (k) { next[k] = cur[k]; });
        next[key] = val;
        this.sel.factory = next;
        this._save();
        this._render(false);
      }

      /* the spec block shows on the factory route only; chips reflect the
         pick, else the first listed value (the default the line posts) */
      _syncFactory(st) {
        var box = this.querySelector('[data-build-factory]');
        if (!box) return;
        var show = st.ship === 'factory' && st.route.source !== 'twinUrl'; /* twin PAGE posts no lines here */
        /* fixwave 33: the fold no longer sits inside the size-gated route block,
           so it waits for a size itself (a frame with a size row only) */
        /* PM 2026-10-25: a made_to_order frame is built to order from the first
           paint — the fold shows before a size; Quick Ship pages still wait */
        var fd = this.data && this.data.frame;
        var mtoFrame = !!(this.s && this.s.frameSource === 'made_to_order');
        if (show && !mtoFrame && fd && fd.sizeIndex >= 0 && !this.sel.size) show = false;
        if (this._linkOut) show = false; /* link_out: never a factory spec here */
        if (box.hidden === show) box.hidden = !show;
        var self = this, current = [], seen = {};
        box.querySelectorAll('[data-build-factory-chip]').forEach(function (btn) {
          var key = btn.getAttribute('data-build-factory-chip');
          var vals = Q.factoryValues(self.data, key);
          var cur = Q.factoryChoice(self.data, self.sel, key);
          if (vals.length && vals.indexOf(cur) === -1) cur = vals[0];
          var on = String(cur) === btn.getAttribute('value');
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (!seen[key]) { seen[key] = true; current.push(String(cur)); }
        });
        /* the folded line reads the current values (PM 2026-10-25: prefilled,
           optional to tune) — "Wide Option · 35cm · Zero" */
        var line = box.querySelector('[data-build-factory-current]');
        if (line) {
          var text = current.join(' · ');
          if (line.textContent !== text) line.textContent = text;
        }
      }

      /* explicit confirm (picker DONE). values default to the staged
         pending choice. Choosing a part un-skips an upsell build. */
      confirmComponent(key, values) {
        var v = values || this._pending[key];
        if (!v) return;
        this.sel[key] = v;
        delete this._pending[key];
        this.sel.skipped = false;
        this._save();
        this._render(false);
      }

      pendingFor(key) { return this._pending[key] || null; }
      selection() { return this.sel; }

      _setSkipped(on) {
        this.sel.skipped = !!on;
        this._save();
        this._render(false);
      }

      _frameVariant(r) {
        if (r.frameVariantId == null) return null;
        var list = r.source === 'twin'
          ? ((this.data.frame.twin || {}).variants || [])
          : (this.data.frame.variants || []);
        for (var k = 0; k < list.length; k++) {
          if (String(list[k].id) === String(r.frameVariantId)) return list[k];
        }
        return null;
      }

      _state() {
        var g = Q.gate(this.data, this.sel);
        var r = Q.resolveRoute(this.data, this.sel);
        var picks = [];
        try { if (g.canBuy) picks = this._picks(); } catch (e) { picks = []; }
        var why = Q.readinessWhy(this.data, this.sel);
        return {
          route: r,
          variant: this._frameVariant(r),
          ship: r.route,
          ready: Q.readiness(this.data, this.sel),
          readyWhy: why.why,
          readyPart: why.part,
          canBuy: g.canBuy,
          missing: g.missing,
          twinUrl: r.source === 'twinUrl' ? ((this.data.frame.twin || {}).url || null) : null,
          picks: picks
        };
      }

      /* the addon lines emitLines would add, without the timestamp noise */
      _picks() {
        var items = Q.emitLines(this.data, this.sel, 0);
        return items.slice(1);
      }

      _cockpitLabel() {
        var c = this.sel.cockpit;
        if (!c || c.width == null || c.stem == null) return null;
        return fmtDim(c.width) + ' · ' + fmtDim(c.stem);
      }

      _render(first) {
        this._quiet = !!first; /* first paint never settles */
        var st = this._state();
        this._hideAddError(); /* a state change restates the world */
        st.totals = this._totals(st);
        this._syncUsd();
        this._syncSpecRows();
        this._syncChips();
        this._syncRoute(st);
        this._syncFactory(st);
        this._syncCards(st);
        this._syncPrices(st);
        this._syncTopline(st);
        this._syncRouteNote(st);
        this._syncAtc(st, first);
        this._syncSkip(first);
        this._syncBoxRow();
        /* self (US frame) and twin (factory) both post a real variant id;
           the twin-PAGE route has none and leaves the input alone */
        if (this.idInput && st.route.frameVariantId != null) {
          this.idInput.value = st.route.frameVariantId;
        }
        this._broadcast(st);
        if (first) this._syncRail();
        this._quiet = false;
      }

      /* the one helper behind every money/label write (see settle()) */
      _settle(el, text) { return settle(el, text, this._quiet); }

      /* colour rail (mobile): when the swatches overflow, bring the selected
         one into view along the rail's own axis. Manual (not scrollIntoView)
         so the page itself never scrolls. */
      _syncRail() {
        var rail = this.querySelector('.qp-build__swatches');
        if (!rail || rail.scrollWidth <= rail.clientWidth) return;
        var sel = rail.querySelector('.qp-qs__swatch.is-selected');
        if (!sel) return;
        var left = sel.offsetLeft, right = left + sel.offsetWidth;
        if (left < rail.scrollLeft) rail.scrollLeft = Math.max(0, left - 16);
        else if (right > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = right - rail.clientWidth + 16;
      }

      /* WHAT'S IN THE BOX cockpit row (block 'dynamic'): the confirmed
         cockpit — riding with the build, not skipped — reads
         cockpitRowValue with {bar} = "<w>mm · <l>mm"; otherwise the
         merchant's static value. Same rule as the bar's state line. */
      /* IN THE BOX dynamic rows (PM 2026-10-23: the bundle lists frame,
         handlebar AND wheelset): a row keyed by component shows that part's
         live choice once it is chosen — cockpit = width · stem through
         cockpitRowValue ({bar}), wheelset = rim depth through wheelsetRowValue
         ({depth}) — else its static merchant value. */
      _syncBoxRow() {
        var self = this;
        var rows = this.querySelectorAll('[data-build-box-row]');
        if (!rows.length) return;
        rows.forEach(function (dd) {
          var key = dd.getAttribute('data-build-box-row') || 'cockpit';
          var comp = self._comp(key);
          var v = comp && !self.sel.skipped ? componentPick(comp, self.sel) : null;
          var text = dd.getAttribute('data-build-box-static') || '';
          if (v) {
            if (key === 'cockpit') {
              var bar = self._cockpitLabel();
              if (bar) text = (self.s.cockpitRowValue || '{bar}').replace('{bar}', bar);
            } else {
              var lab = addonLabel(comp, v);
              var tplKey = key + 'RowValue';
              if (lab && lab.v) text = (self.s[tplKey] || '{' + (key === 'wheelset' ? 'depth' : 'value') + '}').replace('{depth}', lab.v).replace('{value}', lab.v);
            }
          }
          if (dd.textContent !== text) dd.textContent = text;
        });
      }

      /* the synthetic spec row of a picker (groupset drivetrain): its chips
         are the combos listed for the STAGED value of the picker it depends
         on. Hidden until a value with combos is staged. */
      _syncSpecRows() {
        var self = this;
        (this.data.components || []).forEach(function (c) {
          (c.pickers || []).forEach(function (pk) {
            if (!synthetic(pk)) return;
            var dialog = self._pickerEl(c.key);
            var row = dialog ? dialog.querySelector('[data-build-picker-row="' + fieldOf(pk.name) + '"]') : null;
            if (!row) return;
            var vals = specValues(c, pk, self._highlighted(c.key));
            var box = row.querySelector('[data-build-spec-chips]');
            var sig = vals.join('\u0001');
            if (box && box.getAttribute('data-build-spec-sig') !== sig) {
              box.setAttribute('data-build-spec-sig', sig);
              while (box.firstChild) box.removeChild(box.firstChild);
              vals.forEach(function (val) {
                var b = document.createElement('button');
                b.type = 'button';
                b.className = 'qp-build__chip';
                b.setAttribute('data-build-chip', '');
                b.setAttribute('data-option', pk.name);
                b.setAttribute('value', val);
                b.setAttribute('aria-pressed', 'false');
                b.textContent = val;
                box.appendChild(b);
              });
            }
            row.hidden = !vals.length;
          });
        });
      }

      _syncChips() {
        var self = this;
        this.querySelectorAll('[data-build-chip]').forEach(function (btn) {
          var opt = btn.getAttribute('data-option') || '';
          var val = btn.getAttribute('value');
          var picker = btn.closest('[data-build-picker]');
          var cur;
          if (picker) {
            var key = picker.getAttribute('data-build-picker');
            var ch = self._highlighted(key);
            cur = ch ? ch[Q.fieldOf(opt)] : null;
            var pcomp = self._comp(key);
            /* inert ONLY when no buyable variant carries the value at all;
               a value that is buyable elsewhere but not with the other
               axis's highlight stays tappable (visual hint only) — DONE
               still refuses an unbuyable pair. Never a lockout. */
            var out = !self._chipBuyable(pcomp, opt, val, null);
            var pairOut = !out && !self._chipBuyable(pcomp, opt, val, ch);
            btn.classList.toggle('is-soldout', out);
            btn.classList.toggle('is-soldout-pair', pairOut);
            if (out) btn.setAttribute('aria-disabled', 'true');
            else btn.removeAttribute('aria-disabled');
          } else {
            var lc = opt.toLowerCase();
            var isColor = lc.indexOf('color') !== -1 || lc.indexOf('colour') !== -1;
            cur = isColor ? self.sel.color : lc.indexOf('size') !== -1 ? self.sel.size : null;
            if (!isColor && lc.indexOf('size') !== -1) {
              /* combo-level against the selected colour (port of
                 qp-quick-ship.js _syncOptions): no purchasable QS frame
                 variant in this colour + size -> dashed "built for you".
                 A twin-only colour has no frame variant: every size routes. */
              var f = self.data.frame || {}, ci = f.colorIndex, si = f.sizeIndex;
              var routed = !(f.variants || []).some(function (v) {
                return v.options[si] === val && (ci < 0 || v.options[ci] === self.sel.color) && self._avail(v);
              });
              btn.classList.toggle('is-routed', routed);
            }
          }
          var on = cur != null && String(cur) === val;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        /* route legend (under the SIZE row): "· all others built for you" only
           while some size of the current colour is routed (Liquid paints it first) */
        var rest = this.querySelector('[data-build-legend-rest]');
        if (rest) rest.hidden = !this.querySelector('.qp-qs__chip.is-routed');
      }

      _syncTopline(st) {
        if (this.toplineEl) this.toplineEl.setAttribute('data-ship', st.ship);
        if (this.slotEl && this.slotEl.setAttribute) {
          this.slotEl.setAttribute('data-ship', st.ship);
          /* the readiness box: whole-build tier (frame + chosen parts) */
          this.slotEl.setAttribute('data-ready', st.ready);
          /* and WHY it waits — 'parts' swaps the wait tier for the parts tier
             (the frame ships from US stock; a chosen part is built to order) */
          if (st.readyWhy) this.slotEl.setAttribute('data-ready-why', st.readyWhy);
          else this.slotEl.removeAttribute('data-ready-why');
        }
        /* fixwave 34: the readiness subs name the size ({size}; {mto_window}
           too; {part} = the built-to-order part) — Liquid seeds them, the
           engine restates them on every change */
        var size = this.sel.size || '', win = (this.s && this.s.mtoWindow) || '', part = st.readyPart || '';
        (this.slotSubEls || []).forEach(function (el) {
          var tpl = el.getAttribute('data-build-slot-tpl');
          if (tpl == null) return;
          var text = tpl.split('{size}').join(size).split('{mto_window}').join(win).split('{part}').join(part);
          if (el.textContent !== text) el.textContent = text;
        });
      }

      /* size-route note (PM 2026-10-22): a two-state card under the SIZE row,
         open iff a size is selected. In stock (ship us) it is the quiet paper
         "ships now" card; on the factory route (the in-page twin line, or the
         _qs_ twin page) it is the carbon card that says why. While it is open
         it carries the page's ONE lime square (the top line and the stock
         legend step aside). textContent only: merchant copy + option values. */
      _syncRouteNote(st) {
        var el = this.routeNoteEl;
        if (!el) return;
        var open = !!this.sel.size;
        var us = open && st.ship === 'us';
        if (open) {
          var tpl = (us ? this.s.stockNoteText : this.s.routeNoteText) || '';
          var text = tpl
            .split('{size}').join(this.sel.size || '')
            .split('{color}').join(this.sel.color || '')
            .split('{mto_window}').join(this.s.mtoWindow || '');
          if (this.routeNoteTextEl && this.routeNoteTextEl.textContent !== text) this.routeNoteTextEl.textContent = text;
          if (this.routeNoteEyebrowEl) {
            var eb = us ? (this.s.stockNoteEyebrow || this._factoryEyebrow) : this._factoryEyebrow;
            if (this.routeNoteEyebrowEl.textContent !== eb) this.routeNoteEyebrowEl.textContent = eb;
          }
        }
        el.classList.toggle('is-open', open);
        el.classList.toggle('is-us', us);
        if (this.toplineEl) this.toplineEl.classList.toggle('is-note-open', open);
        if (this.legendEl) this.legendEl.hidden = open;
        /* one-shot pulse: only a SIZE pick that lands in built-for-you (the
           celebration stays quiet) */
        if (open && !us && this._pulseNote) this._oneShot(el, 'is-pulse', 700);
        else if (!open || us) el.classList.remove('is-pulse');
      }

      /* add a class that removes itself on animationend (or after ms —
         reduced motion runs no animation, so no animationend fires) */
      _oneShot(el, cls, ms) { oneShot(el, cls, ms); }

      /* SKIP: [hidden] on the cards IS the collapse state (CSS keeps them
         laid out and animates grid-template-rows 1fr -> 0fr, then hides);
         the stub fades in a beat later. The first render never animates. */
      _syncSkip(first) {
        if (this.data.mode !== 'upsell') return;
        var skipped = !!this.sel.skipped;
        var stubWas = this.stubEl ? this.stubEl.hidden : skipped;
        var nodes = [this.cardsEl, this.railCardsEl];
        if (first) {
          nodes.forEach(function (n) { if (n) n.classList.add('is-instant'); });
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(function () {
              nodes.forEach(function (n) { if (n) n.classList.remove('is-instant'); });
            });
          }
        }
        if (this.cardsEl) this.cardsEl.hidden = skipped;
        if (this.railCardsEl) this.railCardsEl.hidden = skipped;
        if (this.stubEl) {
          this.stubEl.hidden = !skipped;
          if (!first && stubWas && skipped) this._oneShot(this.stubEl, 'is-entering', 500);
        }
      }

      /* ------------------------------------------------ cards + picker */
      _comp(key) {
        var cs = this.data.components || [];
        for (var k = 0; k < cs.length; k++) if (cs[k].key === key) return cs[k];
        return null;
      }

      _pickerEl(key) {
        return key ? this.querySelector('[data-build-picker="' + key + '"]') : null;
      }

      /* what the picker shows as chosen: staged -> confirmed -> suggestion */
      _highlighted(key) {
        if (this._pending[key]) return this._pending[key];
        if (isObj(this.sel[key])) return this.sel[key];
        if (key === 'cockpit' && this.sel.cockpitSuggested) return this.sel.cockpitSuggested;
        return null;
      }

      _choiceLabel(comp, variant) {
        var parts = (comp.pickers || []).filter(function (p) {
          return !(typeof p.optionIndex === 'number' && p.optionIndex < 0);
        }).map(function (p) {
          return fmtDim(variant.options[p.optionIndex]);
        });
        /* a spec picker's confirmed combo rides after the options */
        var ch = this.sel[comp.key];
        (comp.pickers || []).forEach(function (p) {
          if (!(typeof p.optionIndex === 'number' && p.optionIndex < 0)) return;
          var got = isObj(ch) ? ch[fieldOf(p.name)] : null;
          if (got != null && String(got) !== '') parts.push(String(got));
        });
        return parts.join(' · ');
      }

      /* a picker chip is buyable when SOME variant carrying it is buyable;
         with hl, the other pickers' highlighted values must match too */
      _chipBuyable(comp, opt, val, hl) {
        if (!comp || !comp.pickers) return true;
        var field = fieldOf(opt);
        var own = comp.pickers.filter(function (pk) { return fieldOf(pk.name) === field; })[0];
        if (own && synthetic(own)) return true; /* spec combos are not stock */
        return (comp.variants || []).some(function (v) {
          for (var p = 0; p < comp.pickers.length; p++) {
            var pk = comp.pickers[p], f = fieldOf(pk.name);
            if (synthetic(pk)) continue;
            var want = f === field ? val : (hl ? hl[f] : null);
            if (want != null && v.options[pk.optionIndex] !== String(want)) return false;
          }
          return buyable(v);
        });
      }

      /* the money on the page. bundle: frame + EVERY component (fixed
         recipe); upsell: frame + the ADDED parts (none once skipped).
         Component = bundleCents where published, else listCents. Frame =
         the resolved variant, else the selected colour's first variant. */
      _totals(st) {
        var f = this.data.frame || {}, ci = f.colorIndex, self = this;
        var fv = st.variant && typeof st.variant.price === 'number' ? st.variant : null;
        if (!fv) {
          var vs = f.variants || [];
          for (var k = 0; k < vs.length && !fv; k++) {
            if ((ci < 0 || vs[k].options[ci] === this.sel.color) && typeof vs[k].price === 'number') fv = vs[k];
          }
          if (!fv && vs.length && typeof vs[0].price === 'number') fv = vs[0];
        }
        if (!fv) return null;
        var cents = fv.price, added = 0;
        /* listCents = the same parts bought apart: the frame variant + each
           counted component at its LIST price (fixwave 29 SAVE tag). Bundle
           prices are USD and go through bundleToPresentment, the same rate
           the Cart Transform applies, so list and total share one currency
           before they are subtracted. */
        var listCents = fv.price;
        var mode = this.data.mode;
        (this.data.components || []).forEach(function (c) {
          var counts = mode === 'bundle' ||
            (mode === 'upsell' && !self.sel.skipped && !!componentPick(c, self.sel));
          if (!counts) return;
          /* list price = the CHOSEN variant's own price (a variant-priced
             product such as the Group Set), else the component's FROM price */
          var pv = componentPick(c, self.sel);
          var lc = pv && typeof pv.price === 'number' ? pv.price : c.listCents;
          var cc = c.bundleCents != null ? bundleToPresentment(c.bundleCents) : lc;
          if (typeof cc === 'number') cents += cc;
          if (typeof lc === 'number') listCents += lc;
          else if (typeof cc === 'number') listCents += cc;
          added++;
        });
        var saveCents = listCents - cents;
        var pct = listCents > 0 && saveCents > 0 ? Math.floor(saveCents * 100 / listCents) : 0;
        return { cents: cents, added: mode === 'upsell' ? added : 0, listCents: listCents, saveCents: saveCents, pct: pct };
      }

      /* server-rendered bundle figures carry data-build-usd-cents (the
         SHOP-currency part) + optional data-build-local-cents (a
         presentment part already summed in, e.g. the frame) and
         data-build-prefix ("+"). First paint may read USD; this restates
         them in the presentment currency on load and every render. */
      _syncUsd() {
        var mf = this.s.moneyFormat, self = this;
        this.querySelectorAll('[data-build-usd-cents]').forEach(function (n) {
          var usd = parseInt(n.getAttribute('data-build-usd-cents'), 10);
          if (!isFinite(usd)) return;
          var local = parseInt(n.getAttribute('data-build-local-cents') || '0', 10) || 0;
          var text = (n.getAttribute('data-build-prefix') || '') + fmtMoney(local + bundleToPresentment(usd), mf);
          self._settle(n, text);
        });
      }

      _syncPrices(st) {
        var t = st.totals;
        if (!t) return;
        var money = fmtMoney(t.cents, this.s.moneyFormat);
        /* bundle mode: the buy-box price IS the computed total */
        var sel = this.data.mode === 'bundle'
          ? '[data-build-atc-price], [data-build-total-value], [data-build-price]'
          : '[data-build-atc-price], [data-build-total-value]';
        var self = this;
        this.querySelectorAll(sel).forEach(function (n) { self._settle(n, money); });
        if (this.data.mode === 'bundle') this._syncSave(t);
        var lbl = this.querySelector('[data-build-total-label]');
        if (lbl) {
          var none = lbl.getAttribute('data-label-none');
          var some = lbl.getAttribute('data-label-some');
          var text = t.added ? (some != null ? some + t.added : null) : none;
          if (text != null) this._settle(lbl, text);
        }
      }

      /* SAVE tag + compare sentence (bundle price block). Both render [hidden];
         they show only while the parts bought apart cost more than the kit
         (saveCents > 0 and a whole percent or more) — never "SAVE $0". The
         copy is the merchant's: this only replaces {amount} / {pct} in the
         tag text and fills the struck list figure. Values settle like price. */
      _syncSave(t) {
        var tag = this.querySelector('[data-build-save]');
        var cmp = this.querySelector('[data-build-compare]');
        if (!tag && !cmp) return;
        var on = t.saveCents > 0 && t.pct >= 1;
        var mf = this.s.moneyFormat;
        /* a whole-dollar figure reads whole on the tag and in the struck
           sentence: "SAVE $319", "$3,718" (PM 2026-10-25, option A); odd
           cents keep their cents */
        var whole = function (c) {
          var m = fmtMoney(c, mf);
          return c % 100 === 0 ? m.replace(/([.,])00(?!\d)/, '') : m;
        };
        if (on) {
          var amount = whole(t.saveCents);
          var fill = function (tpl) { return String(tpl == null ? '' : tpl).split('{amount}').join(amount).split('{pct}').join(String(t.pct)); };
          if (tag) {
            var tx = tag.querySelector('[data-build-save-text]');
            var sr = tag.querySelector('[data-build-save-sr]');
            if (tx) this._settle(tx, fill(this.s.saveTag));
            if (sr) this._settle(sr, fill(this.s.saveSr));
          }
          /* the figure sits in the price row since the 2026-10-25 layout, not
             inside the sentence — find it host-wide */
          var fig = this.querySelector('[data-build-compare-figure]');
          if (fig) this._settle(fig, whole(t.listCents));
        }
        /* the struck apart-price beside the price (PM 2026-10-25 layout) shows
           and hides with the tag line */
        var lst = this.querySelector('[data-build-compare-list]');
        [tag, cmp, lst].forEach(function (n) { if (n) n.hidden = !on; });
      }

      _selectorClick(key, btn) {
        if (this.data.mode === 'upsell' && !this._isAdded(key)) {
          this._toggleAdd(key, btn);
          return;
        }
        this._openPicker(key, btn);
      }

      _isAdded(key) {
        var c = this._comp(key);
        return !!this._added[key] || !!(c && componentPick(c, this.sel));
      }

      /* upsell ring: ADD opens the picker; the part (ring, TOTAL, ATC,
         bar) flips to added only when DONE commits a buyable choice —
         ✕ / Esc / backdrop leave it as it was before the open. A second
         press removes it at once. A pickerless part adds/removes in one
         tap. */
      _toggleAdd(key, trigger) {
        var comp = this._comp(key);
        if (!comp) return;
        if (this._isAdded(key)) {
          delete this.sel[key];
          delete this.sel[key + 'VariantId'];
          delete this._pending[key];
          this._added[key] = false;
          if (this._openKey === key) this._closePicker(key, false);
          this._save();
          this._render(false);
          return;
        }
        if (!comp.pickers || !comp.pickers.length) {
          this._added[key] = true;
          this.sel[key] = true;
          this.sel.skipped = false;
          this._save();
          this._render(false);
          return;
        }
        var card = trigger && trigger.closest ? trigger.closest('[data-build-card]') : null;
        this._openPicker(key, (card && card.querySelector('[data-build-selector]')) || trigger);
      }

      _openPicker(key, opener) {
        var dialog = this._pickerEl(key);
        if (!dialog) return;
        if (this._openKey && this._openKey !== key) this._closePicker(this._openKey, false);
        delete this._pending[key]; /* a dismissed stage never resurfaces */
        this._openKey = key;
        this._opener = opener || this.querySelector('[data-build-selector="' + key + '"]');
        var note = dialog.querySelector('[data-build-picker-note]');
        if (note) note.hidden = true;
        var fitBtn = dialog.querySelector('[data-qs-fit-open]');
        if (fitBtn) fitBtn.hidden = !this._fitTrigger();
        this._render(false);
        var sheet = !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
        dialog.classList.toggle('qp-build__picker--sheet', sheet);
        dialog.classList.toggle('qp-build__picker--popover', !sheet);
        /* a stale popover position must never leak into the sheet */
        dialog.style.top = '';
        dialog.style.left = '';
        /* the pinned bar slides away while a sheet is open: they never stack */
        document.documentElement.classList.toggle('qp-build-sheet-open', sheet);
        if (!dialog.open && !dialog.hasAttribute('open')) {
          if (typeof dialog.showModal === 'function') {
            try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
          } else {
            dialog.setAttribute('open', '');
          }
        }
        if (!sheet) this._place(dialog, this._opener);
        var first = dialog.querySelector('[data-build-chip][aria-pressed="true"]') || dialog.querySelector('[data-build-chip]');
        if (first && first.focus) first.focus();
      }

      /* popover: under the selector, clamped to a 16px viewport gutter;
         flips above when there is no room below */
      _place(dialog, anchor) {
        if (!anchor || !anchor.getBoundingClientRect) return;
        var r = anchor.getBoundingClientRect();
        var vw = window.innerWidth || document.documentElement.clientWidth || 0;
        var vh = window.innerHeight || document.documentElement.clientHeight || 0;
        var w = dialog.offsetWidth || 360, h = dialog.offsetHeight || 0;
        var left = Math.max(16, Math.min(r.left, vw - w - 16));
        var top = r.bottom + 8;
        if (h && top + h > vh - 16 && r.top - 8 - h >= 16) top = r.top - 8 - h;
        if (h && vh) top = Math.max(16, Math.min(top, vh - h - 16)); /* never past the bottom */
        dialog.style.left = Math.round(left) + 'px';
        dialog.style.top = Math.round(top) + 'px';
      }

      /* commit = DONE. Returns false (picker stays open) when a picker is
         unanswered or the combo is not a real variant. */
      _closePicker(key, commit) {
        var dialog = this._pickerEl(key);
        var comp = this._comp(key);
        var note = dialog ? dialog.querySelector('[data-build-picker-note]') : null;
        if (commit && comp) {
          var vals = this._highlighted(key);
          var unanswered = !vals || (comp.pickers || []).some(function (p) {
            if (synthetic(p)) return !specAnswered({ pickers: [p], specs: comp.specs }, vals);
            return vals[fieldOf(p.name)] == null;
          });
          var hit = unanswered ? null : componentVariantFor(comp, vals);
          if (!hit || !buyable(hit)) {
            if (note) {
              note.textContent = unanswered
                ? 'Choose one of each to continue.'
                : 'NOT IN STOCK — PICK ANOTHER';
              note.hidden = false;
            }
            return false;
          }
          var copy = {};
          (comp.pickers || []).forEach(function (p) {
            var f = fieldOf(p.name);
            if (synthetic(p) && !specValues(comp, p, vals).length) return; /* no combos listed: no spec */
            copy[f] = String(vals[f]);
          });
          if (this.data.mode === 'upsell') this._added[key] = true;
          this._openKey = null;
          this.confirmComponent(key, copy);
        } else {
          delete this._pending[key];
          if (this.data.mode === 'upsell' && comp && !componentPick(comp, this.sel)) this._added[key] = false;
        }
        if (note) note.hidden = true;
        this._openKey = null;
        document.documentElement.classList.remove('qp-build-sheet-open');
        if (dialog) {
          /* position classes + inline coords stay until the next open: the
             close transition (240ms sheet / 120ms fade) must not jump layout */
          if (dialog.open || dialog.hasAttribute('open')) {
            if (typeof dialog.close === 'function') dialog.close();
            else dialog.removeAttribute('open');
          }
        }
        var opener = this._opener;
        this._opener = null;
        this._render(false);
        if (opener && opener.focus) opener.focus();
        return true;
      }

      _fitTrigger() {
        var t = this.querySelector('qp-qs-fit [data-qs-fit-open]');
        if (!t) return null;
        var host = t.closest('qp-qs-fit');
        return host && host.hidden ? null : t;
      }

      /* FIND MY SIZE inside a picker: dismiss it, hand off to the buy
         box's size finder (the cockpit suggestion follows the size) */
      _fitFromPicker(key) {
        var t = this._fitTrigger();
        this._closePicker(key, false);
        if (t) t.click();
      }

      _syncCards(st) {
        var self = this;
        var upsell = this.data.mode === 'upsell';
        (this.data.components || []).forEach(function (c) {
          var pick = componentPick(c, self.sel);
          var pickerless = !c.pickers || !c.pickers.length;
          var chosen = !!pick || (!upsell && pickerless);
          if (upsell && pick) self._added[c.key] = true; /* restored / confirmed */
          var added = !upsell || !!self._added[c.key] || !!pick;
          var label = pick && !pickerless ? self._choiceLabel(c, pick) : null;
          var sug = self._suggestLine(c, pick, added);
          self.querySelectorAll('[data-build-card="' + c.key + '"]').forEach(function (card) {
            card.setAttribute('data-build-chosen', chosen ? 'true' : 'false');
            /* confirmed (DONE / added) = the row's ink set-marker; un-add clears it */
            card.classList.toggle('is-set', !!pick);
            var ring = card.querySelector('[data-build-add]');
            if (ring) {
              var on = upsell && added;
              ring.setAttribute('aria-pressed', on ? 'true' : 'false');
              /* visible label reads ADDED (CSS); the name stays the action */
              var nm = ring.getAttribute('data-build-add-name');
              if (nm) ring.setAttribute('aria-label', (on ? 'Remove ' : 'Add ') + nm);
            }
            var btn = card.querySelector('[data-build-selector]');
            if (btn) {
              btn.classList.toggle('is-muted', upsell && !added);
              btn.setAttribute('aria-expanded', self._openKey === c.key ? 'true' : 'false');
              var val = btn.querySelector('[data-build-selector-value]') || btn;
              if (!val.hasAttribute('data-build-empty')) val.setAttribute('data-build-empty', val.textContent);
              var text = label || val.getAttribute('data-build-empty');
              self._settle(val, text);
            }
            var line = card.querySelector('[data-build-suggest]');
            if (line) self._paintSuggest(line, c.key, sug);
            /* list-priced card: the price follows the CHOSEN variant (FROM $999
               → $1,399 for Shimano Ultegra Di2); no pick restores the FROM price */
            var priceEl = card.querySelector('[data-build-card-price]');
            if (priceEl) {
              var listC = parseInt(priceEl.getAttribute('data-build-card-list'), 10);
              var cents = pick && typeof pick.price === 'number' ? pick.price : (isFinite(listC) ? listC : null);
              if (cents != null) {
                self._settle(priceEl, (priceEl.getAttribute('data-build-prefix') || '') + fmtMoney(cents, self.s.moneyFormat));
              }
              var fromEl = card.querySelector('[data-build-card-from]');
              if (fromEl) fromEl.hidden = !!pick;
            }
          });
        });
        var meta = [this.sel.color, this.sel.size].filter(Boolean).join(' · ');
        this.querySelectorAll('[data-build-base-meta]').forEach(function (n) {
          if (meta && n.textContent !== meta) n.textContent = meta;
        });
        if (st.variant && typeof st.variant.price === 'number') {
          var price = fmtMoney(st.variant.price, this.s.moneyFormat);
          var selfP = this;
          this.querySelectorAll('[data-build-base-price]').forEach(function (n) { selfP._settle(n, price); });
        }
      }

      /* §5.5: "SUGGESTED FOR SIZE M · CONFIRMED ✓ · CHANGE" once the
         confirmed pick equals the size suggestion; "… · TAP TO CONFIRM"
         before any confirm; nothing when the rider chose otherwise. */
      _suggestLine(comp, pick, added) {
        var s = this.sel.cockpitSuggested;
        if (comp.key !== 'cockpit' || !s || !this.sel.size || !added) return null;
        var head = 'SUGGESTED FOR SIZE ' + this.sel.size + ' · ';
        if (!pick) return { text: head, action: 'TAP TO CONFIRM' };
        if (String(pickerValue(comp, pick, 'width')) === s.width && String(pickerValue(comp, pick, 'stem')) === s.stem) {
          return { text: head + 'CONFIRMED ✓ · ', action: 'CHANGE' };
        }
        return null;
      }

      _paintSuggest(line, key, sug) {
        var sig = sug ? sug.text + '|' + sug.action : '';
        if (line.getAttribute('data-build-sig') === sig) return;
        line.setAttribute('data-build-sig', sig);
        line.textContent = '';
        line.hidden = !sug;
        if (!sug) return;
        line.appendChild(document.createTextNode(sug.text));
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'qp-build__suggest-action';
        b.setAttribute('data-build-open', key);
        b.textContent = sug.action;
        line.appendChild(b);
      }

      /* An INCOMPLETE build (selections missing) keeps the ATC clickable
         with aria-disabled — the click scrolls to the first missing row.
         Only a build that cannot be bought at all disables it outright. */
      _syncAtc(st, first) {
        var incomplete = !st.canBuy && this._userMissing(st).length > 0;
        /* _qs_ twin-page route — same swap as qp-quick-ship.js's mto
           state: the focusable route <a> replaces the ATC. ONE control at a
           time; the one that appears crossfades in place (both are 52px). */
        var route = !st.canBuy && !!st.twinUrl && !!this.twinCta;
        if (this._linkOut) {
          /* fixwave 34: TWO-TIER — the ATC never hides (disabled, greyed by the
             :disabled grammar) and the route link shows beneath it while the
             size is not buyable here; a buyable size hides the link */
          if (this.atc) {
            this.atc.hidden = false;
            this.atc.disabled = !st.canBuy && !incomplete;
            this.atc.setAttribute('aria-disabled', st.canBuy ? 'false' : 'true');
          }
          if (this.twinCta) {
            if (!first && this.twinCta.hidden === route) this._oneShot(this.twinCta, 'is-entering', 250);
            if (route && this.twinCta.setAttribute) this.twinCta.setAttribute('href', st.twinUrl);
            this.twinCta.hidden = !route;
          }
          /* PM 2026-10-25: a size that cannot be bought here has no order to
             note — the COMMENT box (the optional group) hides with the two-tier
             state and returns with a buyable size */
          var opt = this.querySelector('[data-build-optional]');
          if (opt && opt.hidden !== route) opt.hidden = route;
          return;
        }
        if (!first && this.atc && this.twinCta && this.atc.hidden !== route) {
          this._oneShot(route ? this.twinCta : this.atc, 'is-entering', 250);
        }
        if (this.atc) {
          this.atc.hidden = route;
          this.atc.disabled = !st.canBuy && !incomplete;
          this.atc.setAttribute('aria-disabled', st.canBuy ? 'false' : 'true');
        }
        if (this.twinCta) {
          if (route && this.twinCta.setAttribute) this.twinCta.setAttribute('href', st.twinUrl);
          this.twinCta.hidden = !route;
        }
      }

      /* what the CUSTOMER can still pick — size_code is a recipe gap
         (no picker fixes it), so it disables instead of scrolling */
      _userMissing(st) {
        return st.missing.filter(function (k) { return k !== 'size_code'; });
      }

      /* PM 2026-10-25: the TOTAL · ADDED PARTS label jumps to COMPLETE THE
         BUILD — whichever copy is on screen (the desktop rail or the buy-box
         cards) — and flashes it (one-shot ink outline, the is-missing grammar) */
      _jumpToCards() {
        var shown = function (el) { return !!el && el.getClientRects && el.getClientRects().length > 0; };
        var target = null;
        var cands = [this.querySelector('[data-build-rail-cards]'), this.querySelector('[data-build-cards]')];
        for (var i = 0; i < cands.length && !target; i++) if (shown(cands[i])) target = cands[i];
        if (!target) target = cands[0] || cands[1] || this.querySelector('[data-build-card]');
        if (!target) return;
        if (typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
        }
        this._oneShot(target, 'is-flash', 1400);
      }

      _scrollToMissing(key) {
        var row = this.querySelector('[data-build-row="' + key + '"]');
        if (!row) return;
        this.querySelectorAll('[data-build-row].is-missing').forEach(function (r) { r.classList.remove('is-missing'); });
        row.classList.add('is-missing');
        if (typeof row.scrollIntoView === 'function') {
          row.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
        }
      }

      _submit(e) {
        e.preventDefault();
        if (this._adding) return;
        if (this.commentEl) this.sel.comment = String(this.commentEl.value || '');
        var st = this._state();
        var um = this._userMissing(st);
        if (um.length) { this._scrollToMissing(um[0]); return; }
        if (!st.canBuy) return;
        var items;
        try {
          items = Q.emitLines(this.data, this.sel, Date.now());
        } catch (err) {
          /* recipe misconfiguration (e.g. no size_code for the factory
             route) — never post a half-stamped build */
          console.error('[qp-build] cannot build cart lines', err);
          this._showAddError('retry');
          return;
        }
        var self = this;
        var barAtc = document.querySelector('[data-qs-bar-atc]');
        var buttons = [this.atc, barAtc];
        this._adding = true;
        buttons.forEach(function (b) { if (b) b.disabled = true; });
        /* ADDING… (DOM only — the payload above is already built): the label
           swaps in place, aria-busy marks the wait, no spinner. Restored on
           failure below; a success navigates away. */
        var saved = buttons.map(function (b) { return b ? b.innerHTML : null; });
        buttons.forEach(function (b) {
          if (!b) return;
          b.textContent = 'ADDING…';
          b.setAttribute('aria-busy', 'true');
        });
        var unbusy = function () {
          buttons.forEach(function (b, i) {
            if (!b) return;
            if (saved[i] != null) b.innerHTML = saved[i];
            b.removeAttribute('aria-busy');
          });
        };
        /* kept for the back-forward cache: a page restored from bfcache after
           a successful add would otherwise sit on "ADDING…" (guardian note
           2026-10-25) — the pageshow handler in _setup calls it */
        self._unbusy = unbusy;
        /* 10s timeout: a hung request must never leave the button disabled
           forever with a customer staring at it. AbortController where the
           browser has it; without it the request simply has no timeout
           (old-browser degradation, same as before). */
        var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
        var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 10000) : null;
        fetch(this.s.addUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items }),
          signal: ctrl ? ctrl.signal : undefined
        }).then(function (res) {
          if (timer) clearTimeout(timer);
          if (!res.ok) {
            /* A 422 body carries Shopify's REASON — a validation function's
               own copy (the bundle no-stacking guard) or the stock refusal —
               and that text is written for customers. Read it before throwing
               so the notice can say the truth instead of the generic guess. */
            return res.json().catch(function () { return null; }).then(function (body) {
              var httpErr = new Error('qp-build add failed: HTTP ' + res.status);
              httpErr.status = res.status;
              /* the two cart-add endpoints refuse in different shapes:
                 /cart/add.js -> {status, message, description}
                 /cart/add (routes.cart_add_url, what we post) -> {errors}
                 errors can also be a field->messages object — only a plain
                 string is customer-ready prose; anything else keeps the
                 generic copy. */
              var reason = null;
              if (body) {
                if (typeof body.description === 'string' && body.description) reason = body.description;
                else if (typeof body.errors === 'string' && body.errors) reason = body.errors;
              }
              if (reason) httpErr.description = reason;
              throw httpErr;
            });
          }
          /* 2xx: the items-array add is atomic — a malformed body must
             never read as failure; land on the cart, where the truth is. */
          return res.json().catch(function () { return null; });
        }).then(function () {
          window.location.href = self.s.cartUrl || '/cart';
        }).catch(function (err) {
          if (timer) clearTimeout(timer);
          console.error('[qp-build] cart add failed', err);
          self._adding = false;
          unbusy();
          self._render(false); /* restores true button state */
          var kind = 'retry';
          if (err && err.name === 'AbortError') kind = 'timeout';
          else if (err && err.status >= 400 && err.status < 500) kind = 'taken';
          self._showAddError(kind, err && err.description);
        });
      }

      /* VERBATIM from qp-quick-ship.js except two hook renames: the ATC
         anchor ([data-build-atc] — the notice sits under THIS buy box's
         button) and the re-submit target (this.formEl — an HTMLElement
         must never carry a `form` property). */
      _showAddError(kind, detail) {
        var msg;
        if (typeof detail === 'string' && detail) {
          /* Shopify said WHY in words meant for the customer — use them */
          msg = detail;
        } else if (kind === 'timeout') {
          msg = this.s.addErrorTimeoutText ||
            'That took too long, so we stopped waiting. Check your cart in case it went through, or try again in a moment.';
        } else if (kind === 'retry') {
          msg = this.s.addErrorRetryText ||
            'We could not add this just now. Check your connection and try again in a moment.';
        } else {
          msg = this.s.addErrorText ||
            'We could not add that to your cart just now — the last one may have just been taken. Try another size or colour, or refresh and try again.';
        }
        var host = this.querySelector('[data-qs-add-error]');
        if (!host) {
          host = document.createElement('p');
          host.className = 'qp-qs__adderror';
          host.setAttribute('data-qs-add-error', '');
          host.setAttribute('role', 'alert');
          var atc = this.querySelector('[data-build-atc]');
          if (atc && atc.parentNode) atc.parentNode.insertBefore(host, atc.nextSibling);
          else this.appendChild(host);
        }
        /* When Shopify refused the add AND a discount code is riding on
           the session, the likeliest reason is the bundle no-stacking
           guard — and "remove it to continue" is advice a customer can't
           follow before checkout, because a /discount/CODE link sets an
           INVISIBLE cookie with no UI anywhere. So the notice carries the
           way out: one click drops the code and re-submits the same add.
           If the code wasn't the problem, the retry fails again and says
           the real reason — now without the code muddying it. */
        var offerCodeRemoval = kind === 'taken' && this._hasDiscountCookie();
        var hosts = [host, document.querySelector('[data-qs-bar-error]')];
        var self = this;
        hosts.forEach(function (h) {
          if (!h) return;
          h.textContent = msg;
          if (offerCodeRemoval) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'qp-qs__adderror-action';
            btn.textContent = self.s.addErrorCodeActionText ||
              'Remove the discount code and try again';
            btn.addEventListener('click', function () {
              self._clearDiscountCookie();
              self._hideAddError();
              if (self.formEl && self.formEl.requestSubmit) self.formEl.requestSubmit();
              else self._submit({ preventDefault: function () {} });
            });
            h.appendChild(btn);
          }
          h.hidden = false;
        });
      }

      /* The discount_code cookie is what /discount/CODE marketing links
         and checkout-entered codes leave on the session. It is not
         HttpOnly (verified live), so the storefront can both see it and
         drop it — the ONLY pre-checkout surface where a customer can. */
      _hasDiscountCookie() {
        return /(?:^|;\s*)discount_code=[^;]/.test(document.cookie);
      }

      _clearDiscountCookie() {
        var exp = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        document.cookie = 'discount_code' + exp;
        /* some sessions carry it on the apex domain */
        document.cookie = 'discount_code' + exp + '; domain=.' + location.hostname.replace(/^www\./, '');
      }

      /* stale alerts lie: any selection change (or a fresh submit) restates
         the world, so the previous failure notice comes down with it. */
      _hideAddError() {
        var host = this.querySelector('[data-qs-add-error]');
        if (host) host.hidden = true;
        var barHost = document.querySelector('[data-qs-bar-error]');
        if (barHost) barHost.hidden = true;
      }

      _broadcast(st) {
        var mf = this.s.moneyFormat;
        var v = st.variant;
        var price = v && typeof v.price === 'number' ? fmtMoney(v.price, mf) : null;
        /* joined total once parts ride with the frame: component bundle
           price where the recipe publishes one, else its list price */
        var t = st.totals || this._totals(st);
        var total = t ? fmtMoney(t.cents, mf) : null;
        /* upsell = the live L39ION "priced" frame page: the bar's state
           line reads "+ COCKPIT <w> · <l>" while a cockpit rides with the
           frame, FRAME ONLY otherwise (SKIP / none added) */
        var ck = this._comp('cockpit');
        var cockpitOn = !!ck && !this.sel.skipped && !!componentPick(ck, this.sel);
        var detail = {
          price: price,
          priced: this.data.mode === 'upsell',
          total: total,
          canBuy: st.canBuy,
          mode: this.data.mode,
          cockpit: cockpitOn ? this._cockpitLabel() : null,
          /* in-page route never swaps; only the _qs_ twin-PAGE route
             does (existing detail keys, read by <qp-qs-bar>) */
          route: !st.canBuy && !!st.twinUrl,
          twinUrl: st.twinUrl,
          /* PM 2026-10-25: an INCOMPLETE build keeps the pinned bar's ATC
             clickable (its submit scrolls to the first missing row) — the
             bar reads this itself, so a bar that boots AFTER the first
             broadcast no longer disables the button (init race) */
          incomplete: !st.canBuy && this._userMissing(st).length > 0,
          missing: st.missing.slice(),
          ship: st.ship,
          ready: st.ready,
          color: this.sel.color || null,
          size: this.sel.size || null
        };
        window.__qpQsState = detail;
        document.dispatchEvent(new CustomEvent(EVT, { detail: detail }));
        /* the verbatim pinned bar disables its ATC on !canBuy; an
           INCOMPLETE build keeps it clickable so its submit lands in
           _submit and scrolls to the first missing row */
        var barAtc = document.querySelector('[data-qs-bar-atc]');
        if (barAtc && !this._adding) {
          var incomplete = !st.canBuy && this._userMissing(st).length > 0;
          if (incomplete) barAtc.disabled = false;
          barAtc.setAttribute('aria-disabled', st.canBuy ? 'false' : 'true');
        }
      }
    });
  }

  /* ----------------------------------------------------------------- bar */
  if (!customElements.get('qp-qs-bar')) {
    customElements.define('qp-qs-bar', class extends HTMLElement {
      connectedCallback() {
        if (this._initDone) return;
        this._initDone = true;
        onReady(this._setup.bind(this));
      }

      _setup() {
        this.atc = this.querySelector('[data-qs-bar-atc]');
        this.twin = this.querySelector('[data-qs-bar-twin]');
        this.priceEl = this.querySelector('[data-qs-bar-price]');
        this.stateEl = this.querySelector('[data-qs-bar-state]');
        document.addEventListener(EVT, function (e) { this._apply(e.detail); }.bind(this));
        if (window.__qpQsState) this._apply(window.__qpQsState, true);
        this._watch();
      }

      _apply(d, quiet) {
        if (!d) return;
        /* priced (frame) mode broadcasts a joined TOTAL while a cockpit
           is selected — the pinned bar must quote what the customer
           pays; null under NO COCKPIT, where the frame price IS the pay */
        var p = d.total || d.price;
        if (p && this.priceEl) settle(this.priceEl, p, quiet);
        /* Cockpit state rides with the price (merchant 2026-08-16: the
           bar "needs to consider selected cockpit state" — a joined
           total with no explanation reads as a wrong price). ADDED only
           when the broadcast carries a real joined total, mirroring the
           buy box's own fallback: an unpriceable/routed combo reads
           FRAME ONLY, never a stale claim. Bike (included) pages never
           broadcast priced, so the line stays hidden there. */
        if (this.stateEl) {
          if (d.priced) {
            this.stateEl.textContent = d.total && d.cockpit
              ? '+ COCKPIT ' + d.cockpit
              : 'FRAME ONLY';
            this.stateEl.hidden = false;
          } else {
            this.stateEl.hidden = true;
          }
        }
        /* us/factory route rides detail.ship — detail.mode is the BUILD
           mode (bundle|upsell|plain) on build pages, never a ship route */
        if (d.ship) this.setAttribute('data-ship', d.ship);
        var route = d.route && !!this.twin;
        if (this.atc) {
          this.atc.hidden = route;
          /* an incomplete build stays clickable: the submit lands in the
             engine and scrolls to the first missing row (PM 2026-10-25) */
          this.atc.disabled = !d.canBuy && !d.incomplete;
          this.atc.setAttribute('aria-disabled', d.canBuy ? 'false' : 'true');
        }
        if (this.twin) this.twin.hidden = !route;
      }

      _watch() {
        /* build pages host the buy box in <qp-build-main>; the Quick Ship
           element stays the fallback so this copy also serves QS pages */
        var main = document.querySelector('qp-build-main') || document.querySelector('qp-qs-main');
        if (!main) return; /* no buy box on this page: bar stays hidden */
        var anchor = main.querySelector('[data-qs-atc-anchor]') || main;
        this.hidden = false;
        var self = this;
        if (!('IntersectionObserver' in window)) {
          self.classList.add('is-visible');
          return;
        }
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            /* pin only once the buy-box ATC has scrolled ABOVE the viewport */
            var above = !en.isIntersecting && en.boundingClientRect.top < 0;
            self.classList.toggle('is-visible', above);
          });
        }, { threshold: 0 });
        io.observe(anchor);
      }
    });
  }

  /* ------------------------------------------------------------ size fit */
  /* <qp-qs-fit> — FIND MY SIZE modal (snippets/qp-qs-size-finder.liquid).
     Figma entry 588:8, modal canon 72:53. Height (cm AND ft/in — US
     store) -> size recommendation off the [data-qs-fit-data] map string
     ("XS:155:164|…" — source-agnostic: section blocks today, geometry
     metaobject later). Stock truth is COLOURWAY-AWARE (PM live bug
     2026-08-16: the chip-only read judged the recommendation against the
     selected colour and called a size made-to-order while it sat boxed
     in another colour): current-colour truth still comes LIVE off the
     buy-box size chips' is-routed class so the finder always agrees with
     the chip row, and the main element's [data-qs-data] variant blob
     supplies the other colourways. Verdict ladder — stocked in the
     current colour: select the size, as before; stocked only in other
     colour(s): the answer names them and the primary action switches
     colour AND size; stocked nowhere: made-to-order route (config twin)
     PLUS the nearest boxed size (±1 step) with its colourway named —
     never a dead end, never "out of stock". Actions click the real
     chips and ride the existing qp-qs-main pipeline. role=dialog/
     aria-modal, focus trap, ESC, focus back to the trigger. */
  if (!customElements.get('qp-qs-fit')) {
    var FIT_DOMAIN = { min: 148, max: 203 };
    /* axis ticks are unit-native: whole-foot marks in IN mode, round
       centimetres in CM mode (not inch-derived 152/168/183/198) */
    var FIT_TICKS = {
      in: [
        { cm: 152.4, lbl: "5'0\"" },
        { cm: 167.6, lbl: "5'6\"" },
        { cm: 182.9, lbl: "6'0\"" },
        { cm: 198.1, lbl: "6'6\"" }
      ],
      cm: [
        { cm: 150, lbl: '150' },
        { cm: 165, lbl: '165' },
        { cm: 180, lbl: '180' },
        { cm: 195, lbl: '195' }
      ]
    };

    customElements.define('qp-qs-fit', class extends HTMLElement {
      connectedCallback() {
        if (this._initDone) return;
        this._initDone = true;
        onReady(this._setup.bind(this));
      }

      _setup() {
        /* Failed setup used to leave the server-rendered FIND MY SIZE
           trigger in place as a dead button — a click that does nothing is
           the worst failure mode. Any bail-out below hides the whole
           component instead (the size chips above remain the full,
           working path). CSS pairs this with qp-qs-fit:not(:defined)
           so the trigger never renders when the script itself is absent. */
        var dataEl = this.querySelector('[data-qs-fit-data]');
        if (!dataEl) { this.hidden = true; return; }
        try { this.cfg = JSON.parse(dataEl.textContent); } catch (e) { this.hidden = true; return; }
        this.sizes = [];
        var self = this;
        String(this.cfg.map || '').split('|').forEach(function (r) {
          var p = r.split(':');
          var min = parseFloat(p[1]), max = parseFloat(p[2]);
          if (p[0] && isFinite(min) && isFinite(max) && max > min) {
            self.sizes.push({ label: p[0], min: min, max: max });
          }
        });
        if (!this.sizes.length) { this.hidden = true; return; }

        this.main = this.closest('qp-build-main') || this.closest('qp-qs-main');
        this.trigger = this.querySelector('[data-qs-fit-open]');
        this.overlay = this.querySelector('[data-qs-fit-overlay]');
        this.modal = this.querySelector('[data-qs-fit-modal]');
        this.input = this.querySelector('[data-qs-fit-input]');
        this.msg = this.querySelector('[data-qs-fit-msg]');
        this.chart = this.querySelector('[data-qs-fit-chart]');
        this.result = this.querySelector('[data-qs-fit-result]');
        if (!this.trigger || !this.overlay || !this.modal) { this.hidden = true; return; }
        this.promptHtml = this.result ? this.result.innerHTML : '';

        this.unit = 'in'; /* US store default */
        /* ONE truth at a time (designer ruling): either the rider's
           typed height (heightCm) OR their explicit row pick (picked) —
           never both. Typing clears picked; a row pick clears the height
           field, heightCm AND the stored qp-fit-cm — a pick must never
           fabricate a measurement the rider did not give. (The old code
           kept both alive and the field refused to update while picked
           was set — how the field said 178 while the panel said XS.) */
        this.heightCm = null;
        this.picked = null;
        try {
          var u = window.localStorage.getItem('qp-fit-unit');
          if (u === 'cm' || u === 'in') this.unit = u;
          var h = parseFloat(window.localStorage.getItem('qp-fit-cm'));
          if (isFinite(h)) this.heightCm = h;
        } catch (e) { /* private mode: no memory, still works */ }

        this._keydown = this._onKeydown.bind(this);
        this.trigger.addEventListener('click', this._open.bind(this));
        this.overlay.addEventListener('click', function (e) {
          if (e.target === self.overlay) self._close();
          var close = e.target.closest('[data-qs-fit-close]');
          if (close) { self._close(); return; }
          var unit = e.target.closest('[data-qs-fit-unit]');
          if (unit) { self._setUnit(unit.getAttribute('data-qs-fit-unit')); return; }
          var row = e.target.closest('[data-qs-fit-row]');
          if (row) { self._pickRow(row.getAttribute('data-size')); return; }
          var use = e.target.closest('[data-qs-fit-use]');
          if (use) { self._use(use.getAttribute('data-size'), use.getAttribute('data-color')); }
        });
        if (this.input) {
          this.input.addEventListener('input', function () {
            var raw = self.input.value;
            var cm = self._parse(raw);
            self.heightCm = cm;
            self.picked = null; /* typed height is now the single truth */
            self._msg(cm == null && String(raw).trim() !== '');
            if (cm != null) {
              try { window.localStorage.setItem('qp-fit-cm', String(cm)); } catch (e) {}
            }
            self._renderAll();
          });
          this.input.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            var primary = self.result && self.result.querySelector('[data-qs-fit-use], a.qp-qs-fit__use');
            if (primary) primary.click();
          });
        }
      }

      /* --- live stock read ---------------------------------------------
         CURRENT-colour truth = the buy-box chips' is-routed class (the
         finder must always agree with the chip row the rider can see).
         OTHER-colour truth = the main element's parsed [data-qs-data]
         blob (options[] + available per variant, colorIndex) — the chips
         are combo-level against the selected colour and carry no
         cross-colour signal (the 2026-08-16 colour-blindness bug). The
         blob read respects the PM's force_* preview via main._avail.
         No blob / no colour axis -> _colorsFor returns null and the
         verdict falls back to chip-only, exactly the old behaviour. */
      _chipAt(index, value) {
        if (!this.main || index == null || index < 0) return null;
        var chips = this.main.querySelectorAll('[data-qs-option][data-option-index="' + index + '"]');
        for (var k = 0; k < chips.length; k++) {
          if (chips[k].getAttribute('data-value') === value) return chips[k];
        }
        return null;
      }
      _chipFor(size) { return this._chipAt(this.cfg.sizeIndex, size); }
      _stocked(size) {
        var chip = this._chipFor(size);
        return !!chip && !chip.classList.contains('is-routed');
      }
      _mainData() {
        /* build host: adapter over data.frame (see qp-build-main) */
        if (this.main && typeof this.main._mainData === 'function') return this.main._mainData();
        var d = this.main && this.main.data;
        return d && d.variants && d.variants.length ? d : null;
      }
      _availV(v) {
        return this.main && this.main._avail ? this.main._avail(v) : !!v.available;
      }
      _currentColor() {
        var d = this._mainData();
        if (!d || d.colorIndex == null || d.colorIndex < 0) return null;
        if (d.currentColor !== undefined) return d.currentColor;
        return this.main.selection ? this.main.selection[d.colorIndex] : null;
      }
      /* ordered, deduped colourway values in which `size` is purchasable;
         null = no colour axis or no blob (caller falls back to chips) */
      _colorsFor(size) {
        var d = this._mainData();
        if (!d || d.colorIndex == null || d.colorIndex < 0 || this.cfg.sizeIndex < 0) return null;
        var ci = d.colorIndex, si = this.cfg.sizeIndex;
        var self = this, out = [], seen = {};
        d.variants.forEach(function (v) {
          var c = v.options[ci];
          if (v.options[si] !== size || seen[c] || !self._availV(v)) return;
          seen[c] = true;
          out.push(c);
        });
        return out;
      }
      /* one verdict per size: current  — boxed in the selected colourway
         (chip truth); others — colourways where it is boxed instead */
      _verdict(size) {
        var colors = this._colorsFor(size);
        var cur = this._currentColor();
        if (colors == null) return { current: this._stocked(size), others: [] };
        return {
          current: this._stocked(size),
          others: colors.filter(function (c) { return c !== cur; })
        };
      }
      /* "Diamond Black" / "A and B" / "A, B and C" / "A, B, C and 2 more" */
      _listColors(arr) {
        var shown = arr.slice(0, 3), extra = arr.length - shown.length;
        if (extra > 0) return shown.join(', ') + ' and ' + extra + ' more';
        if (shown.length === 1) return shown[0];
        return shown.slice(0, -1).join(', ') + ' and ' + shown[shown.length - 1];
      }
      _fill(tpl, map) {
        return String(tpl == null ? '' : tpl).replace(/\{(\w+)\}/g, function (m, k) {
          return map[k] != null ? map[k] : m;
        });
      }

      /* --- height parsing / formatting --------------------------------- */
      _parse(raw) {
        raw = String(raw == null ? '' : raw).trim();
        this._outOfRange = false;
        if (!raw) return null;
        var cm;
        if (this.unit === 'cm') {
          cm = parseFloat(raw.replace(',', '.'));
        } else {
          var m = raw.match(/^(\d+(?:\.\d+)?)\s*(?:'|′|ft|feet)?\s*(\d+(?:\.\d+)?)?\s*(?:\"|″|in|inches)?$/i);
          if (!m) return null;
          var a = parseFloat(m[1]);
          var b = m[2] != null ? parseFloat(m[2]) : null;
          var inches;
          if (b != null) inches = a * 12 + b;      /* 5'10, 5 10, 5ft10 */
          else if (a <= 8) inches = a * 12;        /* bare feet          */
          else if (a >= 120 && a <= 230) return a; /* bare 120–230 in IN
              mode is centimetres — nobody is 120 inches tall (P0: a
              metric rider typing 178 must not get silent nothing) */
          else inches = a;                         /* total inches (70)  */
          cm = inches * 2.54;
        }
        if (isFinite(cm) && (cm < 120 || cm > 230)) {
          /* a real number, just not a rider height we fit — a different
             fact than "could not read that", so say a different thing */
          this._outOfRange = true;
          return null;
        }
        return isFinite(cm) ? cm : null;
      }
      _ftIn(cm) {
        var t = Math.round(cm / 2.54), f = Math.floor(t / 12);
        return f + "'" + (t - f * 12) + '"';
      }
      _fmtHeight(cm) {
        return this.unit === 'cm' ? Math.round(cm) + ' cm' : this._ftIn(cm);
      }
      _rangeLabel(s) {
        return this.unit === 'cm'
          ? s.min + '–' + s.max + ' CM'
          : this._ftIn(s.min) + '–' + this._ftIn(s.max);
      }

      /* explicit pick becomes the single truth: field, heightCm and the
         stored height are CLEARED (designer ruling — never fabricate a
         measurement the rider did not give), so field and panel can
         never contradict each other. */
      _pickRow(label) {
        for (var i = 0; i < this.sizes.length; i++) {
          if (this.sizes[i].label !== label) continue;
          this.picked = label;
          this.heightCm = null;
          if (this.input) this.input.value = '';
          this._msg(false);
          try { window.localStorage.removeItem('qp-fit-cm'); } catch (e) {}
          this._renderAll();
          return;
        }
      }

      /* invalid-entry help line — visible + aria-live, never colour-only
         (the message's presence IS the signal) */
      _msg(show) {
        if (!this.msg) return;
        var t = '';
        if (show) {
          if (this._outOfRange) {
            /* the number read fine — it is simply outside the heights we
               fit. Not the customer's fault; give them a human next step. */
            t = 'That height is outside the range we fit — check the number, or talk to us and we’ll match you by hand.';
          } else {
            t = this.unit === 'cm'
              ? 'Enter a height in cm — like 178.'
              : 'Enter a height like 5\'10" — or switch to CM.';
          }
        }
        this.msg.textContent = t;
      }

      _recommend() {
        var i;
        if (this.picked) {
          for (i = 0; i < this.sizes.length; i++) {
            if (this.sizes[i].label === this.picked) return { size: this.sizes[i], edge: false, picked: true };
          }
        }
        var cm = this.heightCm;
        if (cm == null) return null;
        /* official ranges OVERLAP (e.g. 164-174 / 171-182): collect every
           row whose [min,max] contains the height, then recommend the one
           whose range CENTER sits nearest the input — tie goes to the
           larger size (sizes is ascending by min, so <= keeps the last,
           i.e. largest, match on a tie). The "between sizes" caution line
           already covers the ambiguity for the rider. */
        var matches = [];
        for (i = 0; i < this.sizes.length; i++) {
          if (cm >= this.sizes[i].min && cm <= this.sizes[i].max) matches.push(this.sizes[i]);
        }
        if (matches.length) {
          var bestMatch = matches[0], bestMatchD = Infinity;
          matches.forEach(function (s) {
            var d = Math.abs((s.min + s.max) / 2 - cm);
            if (d <= bestMatchD) { bestMatchD = d; bestMatch = s; }
          });
          return { size: bestMatch, edge: false };
        }
        if (cm < this.sizes[0].min) return { size: this.sizes[0], edge: true };
        var last = this.sizes[this.sizes.length - 1];
        if (cm > last.max) return { size: last, edge: true };
        /* between two ranges (gapped merchant data): nearest midpoint */
        var best = this.sizes[0], bd = Infinity;
        this.sizes.forEach(function (s) {
          var d = Math.abs((s.min + s.max) / 2 - cm);
          if (d < bd) { bd = d; best = s; }
        });
        return { size: best, edge: false };
      }

      /* fit-safety (designer P0): only ever offer a stocked size ONE
         step from the recommendation, sizing DOWN preferred (stem/post
         fit up). Two or more steps off is a mis-fit — the made-to-order
         route stands alone. Colourway-aware (2026-08-16): a step counts
         when boxed in ANY colourway; fit outranks colour, so a size-down
         in another colour beats a size-up in the current one. Returns
         { size, color, switchColor } — color null only when the product
         has no colour axis. */
      _nearestStocked(label) {
        var idx = -1, i, k, cand, v;
        for (i = 0; i < this.sizes.length; i++) {
          if (this.sizes[i].label === label) { idx = i; break; }
        }
        if (idx < 0) return null;
        var steps = [idx - 1, idx + 1];
        for (k = 0; k < steps.length; k++) {
          if (steps[k] < 0 || steps[k] >= this.sizes.length) continue;
          cand = this.sizes[steps[k]];
          v = this._verdict(cand.label);
          if (v.current) return { size: cand, color: this._currentColor(), switchColor: false };
          if (v.others.length) return { size: cand, color: v.others[0], switchColor: true };
        }
        return null;
      }

      /* --- rendering ---------------------------------------------------- */
      _pct(cm) {
        var p = (cm - FIT_DOMAIN.min) / (FIT_DOMAIN.max - FIT_DOMAIN.min) * 100;
        return Math.max(0, Math.min(100, p));
      }

      _renderChart(rec) {
        if (!this.chart) return;
        var self = this;
        var html = '<div class="qp-qs-fit__axis" aria-hidden="true">';
        FIT_TICKS[this.unit === 'cm' ? 'cm' : 'in'].forEach(function (t) {
          html += '<span class="qp-qs-fit__tick" style="left:' + self._pct(t.cm).toFixed(1) + '%">'
            + esc(t.lbl) + '</span>';
        });
        html += '</div>';
        this.sizes.forEach(function (s) {
          var sel = !!(rec && rec.size.label === s.label);
          var stocked = self._stocked(s.label);
          var left = self._pct(s.min), right = self._pct(s.max);
          var cls = 'qp-qs-fit__row' + (sel ? ' is-selected' : '') + (stocked ? '' : ' is-routed');
          var tag = '';
          if (sel) {
            tag = right > 72
              ? '<span class="qp-qs-fit__sel-tag" style="right:calc(' + (100 - left).toFixed(1) + '% + 4px)">Selected</span>'
              : '<span class="qp-qs-fit__sel-tag" style="left:calc(' + right.toFixed(1) + '% + 4px)">Selected</span>';
          }
          html += '<button type="button" class="' + cls + '" data-qs-fit-row'
            + ' data-size="' + escAttr(s.label) + '" aria-pressed="' + (sel ? 'true' : 'false') + '"'
            + ' aria-label="Size ' + escAttr(s.label) + ', rider ' + escAttr(self._rangeLabel(s)) + '">'
            + '<span><span class="qp-qs-fit__size">' + esc(s.label) + '</span>'
            + '<span class="qp-qs-fit__range">' + esc(self._rangeLabel(s)) + '</span></span>'
            + '<span class="qp-qs-fit__track">'
            + '<span class="qp-qs-fit__bar" style="left:' + left.toFixed(1) + '%;width:' + Math.max(2, right - left).toFixed(1) + '%"></span>'
            + tag + '</span></button>';
        });
        this.chart.innerHTML = html;
      }

      _renderResult(rec) {
        if (!this.result) return;
        if (!rec) { this.result.innerHTML = this.promptHtml; return; }
        var s = rec.size;
        var win = this.cfg.mtoWindow || '';
        /* Panel order (PM hierarchy ruling 2026-08-16): (1) the
           recommendation, (2) the availability answer, (3) the
           action(s), (4) the fit caution LAST as a footnote — the
           decision-relevant fact must outrank generic advice. */
        /* label honesty (designer ruling): height-derived = RECOMMENDED,
           explicit pick = YOU PICKED — never "your fit", we did not
           perform a fitting. Both merchant-editable. */
        var label = rec.picked
          ? (this.cfg.labelPicked || 'You picked')
          : (this.cfg.labelRec || 'Recommended');
        var html = '<p class="qp-qs-fit__fitline">' + esc(label) + ' · ' + esc(s.label)
          + ' · Rider ' + esc(this._rangeLabel(s)) + '</p>';
        if (rec.edge) {
          html += '<p class="qp-qs-fit__mtoline">You’re at the edge of our range — '
            + esc(s.label) + ' is the closest fit. Talk to us if you’re unsure.</p>';
        }
        /* colourway-aware availability answer + actions. CTA weight (PM
           ruling): whenever something is boxed — this size in another
           colour, or the nearest size — the boxed route is the PRIMARY
           action and made-to-order drops to the quiet register; made-to-
           order is primary only when nothing is boxed. */
        var v = this._verdict(s.label);
        var actions = '';
        if (v.current) {
          html += '<p class="qp-qs-fit__mtoline">'
            + esc(this._fill(this.cfg.lineStocked, { size: s.label })) + '</p>';
          actions += '<button type="button" class="qp-qs-fit__use" data-qs-fit-use data-size="'
            + escAttr(s.label) + '">Use size ' + esc(s.label) + ' →</button>';
        } else if (v.others.length) {
          /* boxed in other colourway(s): name them (cap 3, then "and N
             more"); each action switches colour AND size in the buy box */
          html += '<p class="qp-qs-fit__mtoline">'
            + esc(this._fill(this.cfg.lineOtherColor, { size: s.label, colors: this._listColors(v.others) }))
            + '</p>';
          var self = this;
          v.others.slice(0, 3).forEach(function (c, n) {
            actions += '<button type="button" class="qp-qs-fit__use'
              + (n === 0 ? '' : ' qp-qs-fit__use--quiet') + '" data-qs-fit-use data-size="'
              + escAttr(s.label) + '" data-color="' + escAttr(c) + '">'
              + esc(self._fill(self.cfg.actionOtherColor, { size: s.label, color: c }))
              + (n === 0 ? ' →' : '') + '</button>';
          });
          if (this.cfg.twinUrl) {
            actions += '<a class="qp-qs-fit__use qp-qs-fit__use--quiet" href="' + escAttr(this.cfg.twinUrl)
              + '">' + esc(this._fill(this.cfg.actionMtoQuiet, { window: win })) + '</a>';
          }
        } else {
          /* boxed in NO colourway — only now made-to-order, and NEVER
             "out of stock"; never a dead end: the nearest boxed size
             (±1 step) rides along with its colourway named. When that
             nearest size exists it is the PRIMARY action (something IS
             boxed) and made-to-order goes quiet; made-to-order leads
             only when nothing is boxed at all. */
          html += '<p class="qp-qs-fit__mtoline">Size ' + esc(s.label)
            + ' is made to your order right now'
            + (win ? ' — about ' + esc(win) : '') + '.</p>';
          var near = this._nearestStocked(s.label);
          if (near) {
            var nearLbl = near.color
              ? this._fill(this.cfg.actionNearestColor, { size: near.size.label, color: near.color })
              : 'Take size ' + near.size.label + ' — boxed now';
            actions += '<button type="button" class="qp-qs-fit__use" data-qs-fit-use data-size="'
              + escAttr(near.size.label) + '"'
              + (near.switchColor ? ' data-color="' + escAttr(near.color) + '"' : '')
              + '>' + esc(nearLbl) + '</button>';
          }
          if (this.cfg.twinUrl) {
            actions += '<a class="qp-qs-fit__use' + (near ? ' qp-qs-fit__use--quiet' : '')
              + '" href="' + escAttr(this.cfg.twinUrl)
              + '">Build it made to order →</a>';
          }
        }
        if (actions) html += '<div class="qp-qs-fit__actions">' + actions + '</div>';
        /* (4) fit caution — stays, but as the closing footnote */
        if (this.cfg.caution) {
          html += '<p class="qp-qs-fit__caution">' + esc(this.cfg.caution) + '</p>';
        }
        this.result.innerHTML = html;
      }

      _renderAll() {
        var rec = this._recommend();
        this._renderChart(rec);
        this._renderResult(rec);
      }

      /* --- unit toggle --------------------------------------------------- */
      _setUnit(u) {
        if (u !== 'cm' && u !== 'in') return;
        this.unit = u;
        try { window.localStorage.setItem('qp-fit-unit', u); } catch (e) {}
        this._applyUnit();
        /* an entry that was invalid under the old unit gets re-read
           under the new one (e.g. 5'10" typed while CM was active) */
        if (this.input && this.heightCm == null && !this.picked && this.input.value.trim() !== '') {
          this.heightCm = this._parse(this.input.value);
          if (this.heightCm != null) {
            try { window.localStorage.setItem('qp-fit-cm', String(this.heightCm)); } catch (e) {}
          }
          this._msg(this.heightCm == null);
        }
        this._renderAll();
      }
      _applyUnit() {
        var self = this;
        this.overlay.querySelectorAll('[data-qs-fit-unit]').forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-qs-fit-unit') === self.unit ? 'true' : 'false');
        });
        if (this.input) {
          /* the placeholder is an EXAMPLE, never a value: the "e.g."
             prefix carries that meaning even where the muted
             ::placeholder colour can't be perceived (PM fix 2026-08-16) */
          this.input.placeholder = this.unit === 'cm' ? 'e.g. 178' : 'e.g. 5\' 10"';
          if (this.heightCm != null) this.input.value = this._fmtHeight(this.heightCm).replace(' cm', '');
        }
      }

      /* --- dialog plumbing ---------------------------------------------- */
      /* P0 stacking, part 2: the chat teaser mounts as an ANONYMOUS fixed
         body child (no id/class — unreachable from CSS) at z 999999 and
         paints over the modal footer at 390. While the dialog is open,
         hide any third-party fixed iframe holder stacked above the
         overlay (z 84); restore exactly on close. The known-id offenders
         (#qpu-launcher etc.) are handled in CSS via body.qp-qs-fit-open. */
      _suppressOverlays() {
        this._suppressed = [];
        var kids = document.body.children;
        for (var k = 0; k < kids.length; k++) {
          var el = kids[k];
          if (el.contains(this) || el.id === 'PBarNextFrameWrapper') continue;
          if (!el.querySelector || !el.querySelector('iframe')) continue;
          var high = false;
          var nodes = [el].concat(Array.prototype.slice.call(el.querySelectorAll('*'), 0, 50));
          for (var j = 0; j < nodes.length; j++) {
            var cs = window.getComputedStyle(nodes[j]);
            var z = parseInt(cs.zIndex, 10);
            if (cs.position === 'fixed' && isFinite(z) && z > 84) { high = true; break; }
          }
          if (!high) continue;
          this._suppressed.push({ el: el, prev: el.style.visibility });
          el.style.setProperty('visibility', 'hidden', 'important');
        }
      }
      _restoreOverlays() {
        (this._suppressed || []).forEach(function (s) {
          if (s.prev) s.el.style.setProperty('visibility', s.prev);
          else s.el.style.removeProperty('visibility');
        });
        this._suppressed = [];
      }

      _open() {
        this.overlay.hidden = false;
        var ov = this.overlay;
        if (reducedMotion()) ov.classList.add('is-open');
        else requestAnimationFrame(function () { ov.classList.add('is-open'); });
        document.body.classList.add('qp-qs-fit-open');
        this._suppressOverlays();
        document.addEventListener('keydown', this._keydown, true);
        this._applyUnit();
        this._renderAll();
        if (this.input) this.input.focus();
      }

      _close() {
        this.overlay.classList.remove('is-open');
        this.overlay.hidden = true;
        document.body.classList.remove('qp-qs-fit-open');
        this._restoreOverlays();
        document.removeEventListener('keydown', this._keydown, true);
        if (this.trigger) this.trigger.focus(); /* spec: focus returns here */
      }

      _use(size, color) {
        /* colour FIRST (data-color = a cross-colourway offer): the size
           chip's routed/stocked read is combo-level against the selected
           colour, so the colourway must land before the size does. Both
           clicks ride the existing qp-qs-main pipeline — no state is
           written here. If a chip has gone (buy-box DOM drift, a
           colourway pulled between render and click), closing silently
           would read as "the button did nothing" — say so instead and
           keep the modal open so the message is actually seen. */
        var applied = true;
        if (color) {
          var d = this._mainData();
          var cchip = d ? this._chipAt(d.colorIndex, color) : null;
          if (cchip) {
            if (!cchip.classList.contains('is-selected')) cchip.click();
          } else {
            applied = false;
          }
        }
        if (size) {
          var chip = this._chipFor(size);
          if (chip) chip.click();
          else applied = false;
        }
        if (!applied) {
          if (this.msg) {
            this.msg.textContent = 'We could not set that for you — close this window and pick size '
              + size + ' in the size row above.';
          }
          return;
        }
        this._close();
      }

      _onKeydown(e) {
        if (this.overlay.hidden) return;
        if (e.key === 'Escape') { e.preventDefault(); this._close(); return; }
        if (e.key !== 'Tab') return;
        /* focus trap — modal is the only reachable surface while open */
        var els = this.modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        var list = [];
        for (var k = 0; k < els.length; k++) {
          if (els[k].offsetParent !== null || els[k] === document.activeElement) list.push(els[k]);
        }
        if (!list.length) return;
        var first = list[0], last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (!this.modal.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      }
    });
  }
})();
